import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, BarChart, Bar,
  LineChart, Line, ComposedChart,
} from "recharts";
import axios from "axios";
import API_BASE_URL from "../config";
import AdminNavbar from "../Layout/AdminNavbar";
import {
  FaClipboardCheck, FaChevronDown, FaChevronRight,
  FaTasks, FaCheckCircle, FaClock, FaExclamationTriangle,
  FaUserSlash, FaFire, FaBolt, FaUsers, FaTh,
  FaProjectDiagram, FaRocket, FaChartBar, FaCalendarAlt,
} from "react-icons/fa";
import { BsLightningChargeFill } from "react-icons/bs";

// ─── Design tokens ───────────────────────────────────────────
const T = {
  bg: "#f8fafc",
  white: "#ffffff",
  border: "#e2e8f0",
  shadow: "0 1px 3px rgba(15,23,42,0.06), 0 0 0 1px rgba(15,23,42,0.04)",
  shadowMd: "0 4px 12px rgba(15,23,42,0.08)",
  radius: 12,
  text1: "#0f172a",
  text2: "#475569",
  text3: "#94a3b8",
  primary: "#6366f1",
  navBg: "#0f172a",
  navBorder: "#1e293b",
};

// ─── Domain constants ─────────────────────────────────────────
const OPEN_STATUSES = ["PENDING", "IN_PROGRESS", "READY TO REVIEW", "READY TO TEST", "IN TESTING"];
const TOP_TABS = ["Dashboard", "Team", "Projects", "Sprints", "Timesheets", "Reports"];
const TAB_ICONS = {
  Dashboard:  FaTh,
  Projects:   FaProjectDiagram,
  Sprints:    FaRocket,
  Timesheets: FaCalendarAlt,
  Reports:    FaChartBar,
  Team:       FaUsers,
};
const DASH_TABS = ["Overview", "Weekly Trends", "SLA Performance", "Monthly Comparison", "Weekend Analysis"];
const TIME_OPTS = ["All", "6M", "3M", "1M", "1W"];
const TIME_DAYS = { "6M": 180, "3M": 90, "1M": 30, "1W": 7 };

const STATUS_COLORS = {
  PENDING:         "#f59e0b",
  IN_PROGRESS:     "#3b82f6",
  "READY TO REVIEW": "#8b5cf6",
  "READY TO TEST":   "#f97316",
  "IN TESTING":      "#06b6d4",
  COMPLETED:       "#10b981",
  CANCELLED:       "#9ca3af",
  REVIEW:          "#8b5cf6", // legacy fallback
};
const PRIORITY_COLORS = { High: "#ef4444", Medium: "#f59e0b", Low: "#10b981" };
const TYPE_COLORS = ["#6366f1", "#8b5cf6", "#ec4899", "#f43f5e", "#f59e0b", "#10b981", "#06b6d4", "#3b82f6"];

const STATUS_BADGE = {
  PENDING:           { bg: "#fffbeb", color: "#92400e" },
  IN_PROGRESS:       { bg: "#eff6ff", color: "#1d4ed8" },
  "READY TO REVIEW": { bg: "#f5f3ff", color: "#6d28d9" },
  "READY TO TEST":   { bg: "#fff7ed", color: "#c2410c" },
  "IN TESTING":      { bg: "#ecfeff", color: "#0e7490" },
  COMPLETED:         { bg: "#f0fdf4", color: "#166534" },
  CANCELLED:         { bg: "#f8fafc", color: "#64748b" },
  REVIEW:            { bg: "#f5f3ff", color: "#6d28d9" }, // legacy fallback
};
const PRIORITY_BADGE = {
  High: { bg: "#fef2f2", color: "#dc2626" },
  Medium: { bg: "#fffbeb", color: "#b45309" },
  Low: { bg: "#f0fdf4", color: "#166534" },
};

// ─── Normalizers (fix inconsistent casing / typos from API) ──
const PRIORITY_NORM = { HIGH: "High", MEDIUM: "Medium", LOW: "Low" };
function normalizePriority(p) {
  if (!p) return "None";
  return PRIORITY_NORM[p.toUpperCase()] || p;
}
const STATUS_NORM = { "REAY TO TEST": "READY TO TEST", "REVIEW": "READY TO REVIEW" };
function normalizeStatus(s) {
  if (!s) return s;
  const t = s.trim();
  return STATUS_NORM[t] || t;
}

// ─── Helpers ──────────────────────────────────────────────────
function isoWeekLabel(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dn = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dn);
  const ys = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return `${d.getUTCFullYear()}-W${String(Math.ceil(((d - ys) / 86400000 + 1) / 7)).padStart(2, "0")}`;
}
function buildWeekBuckets(n) {
  const now = new Date(), buckets = {}, order = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now); d.setDate(d.getDate() - i * 7);
    const lb = isoWeekLabel(d);
    if (!(lb in buckets)) { buckets[lb] = null; order.push(lb); }
  }
  return { buckets, order };
}
function monthLabel(date) {
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// ─── Micro-components ─────────────────────────────────────────
const SLabel = ({ children }) => (
  <span style={{ fontSize: "0.65rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.08em" }}>
    {children}
  </span>
);

const StatCard = ({ label, value, sub, accent, Icon }) => (
  <div style={{
    background: T.white, borderRadius: T.radius,
    padding: "18px 20px 16px", boxShadow: T.shadow,
    borderTop: `3px solid ${accent}`, height: "100%",
    transition: "box-shadow 0.2s",
  }}
    onMouseEnter={e => e.currentTarget.style.boxShadow = T.shadowMd}
    onMouseLeave={e => e.currentTarget.style.boxShadow = T.shadow}
  >
    <div className="d-flex align-items-start justify-content-between mb-2">
      <SLabel>{label}</SLabel>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: accent + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={14} color={accent} />
      </div>
    </div>
    <div style={{ fontSize: "2rem", fontWeight: 800, color: T.text1, lineHeight: 1.1, marginBottom: 4 }}>{value}</div>
    <div style={{ fontSize: "0.73rem", color: T.text3 }}>{sub}</div>
  </div>
);

const Chip = ({ children, bg, color }) => (
  <span style={{ background: bg, color, borderRadius: 20, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700, display: "inline-block", letterSpacing: "0.01em" }}>
    {children}
  </span>
);

const Card = ({ children, style = {} }) => (
  <div style={{ background: T.white, borderRadius: T.radius, padding: "20px 24px", boxShadow: T.shadow, ...style }}>
    {children}
  </div>
);

const CardHead = ({ label, right }) => (
  <div className="d-flex align-items-center justify-content-between mb-4">
    <SLabel>{label}</SLabel>
    {right}
  </div>
);

const ttStyle = {
  contentStyle: { fontSize: 12, borderRadius: 8, border: `1px solid ${T.border}`, boxShadow: T.shadowMd, padding: "8px 12px", color: T.text2 },
  labelStyle: { fontWeight: 700, color: T.text1, marginBottom: 2 },
};

const PillSelect = ({ value, onChange, options, active }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const selected = options.find(o => o.value === value) || options[0];

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "5px 10px 5px 12px", borderRadius: 6,
          border: `1.5px solid ${active ? T.primary : T.border}`,
          background: active ? "#eef2ff" : T.white,
          color: active ? T.primary : T.text2,
          fontSize: "0.78rem", fontWeight: active ? 600 : 500,
          cursor: "pointer", outline: "none", whiteSpace: "nowrap",
          boxShadow: open ? `0 0 0 3px ${T.primary}22` : "none",
          transition: "all 0.15s",
        }}
      >
        <span>{selected?.label}</span>
        <FaChevronDown size={9} style={{
          color: active ? T.primary : T.text3, flexShrink: 0,
          transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)",
        }} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0,
          minWidth: Math.max(ref.current?.offsetWidth || 0, 160),
          maxHeight: 260, overflowY: "auto",
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: 10,
          boxShadow: "0 8px 24px rgba(15,23,42,0.13), 0 2px 8px rgba(15,23,42,0.06)",
          zIndex: 9999, padding: "5px",
        }}>
          {options.map(opt => {
            const isSel = opt.value === value;
            return (
              <div
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                  padding: "7px 10px", borderRadius: 6,
                  fontSize: "0.8rem", fontWeight: isSel ? 600 : 400,
                  color: isSel ? T.primary : T.text1,
                  background: isSel ? "#eef2ff" : "transparent",
                  cursor: "pointer", transition: "background 0.1s", whiteSpace: "nowrap",
                }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = T.bg; }}
                onMouseLeave={e => { e.currentTarget.style.background = isSel ? "#eef2ff" : "transparent"; }}
              >
                <span>{opt.label}</span>
                {isSel && <FaCheckCircle size={10} color={T.primary} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ToggleGroup = ({ options, value, onChange }) => (
  <div style={{ display: "inline-flex", border: `1.5px solid ${T.border}`, borderRadius: 6, overflow: "hidden", background: T.white }}>
    {options.map(opt => (
      <button key={opt} onClick={() => onChange(opt)} style={{
        padding: "4px 12px", fontSize: "0.75rem", border: "none",
        background: value === opt ? T.primary : "transparent",
        color: value === opt ? T.white : T.text3,
        cursor: "pointer", fontWeight: value === opt ? 600 : 500,
        transition: "all 0.15s",
      }}>{opt}</button>
    ))}
  </div>
);

// ═════════════════════════════════════════════════════════════
const TasksLandingPage = () => {
  const navigate = useNavigate();

  const [tasks,    setTasks]    = useState([]);
  const [worklogs, setWorklogs] = useState([]);
  const [sprints,  setSprints]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const sprintsFetched = useRef(false);

  // Top-level navigation
  const [activeTopTab, setActiveTopTab] = useState("Dashboard");
  const [activeDashTab, setActiveDashTab] = useState("Overview");
  const [tabSwitching, setTabSwitching] = useState(false);

  const switchTopTab = useCallback((tab) => {
    setTabSwitching(true);
    setActiveTopTab(tab);
    setExpandedId(null);
    setTimeout(() => setTabSwitching(false), 180);
  }, []);

  const switchDashTab = useCallback((tab) => {
    setTabSwitching(true);
    setActiveDashTab(tab);
    setTimeout(() => setTabSwitching(false), 180);
  }, []);

  // Global filters (Dashboard)
  const [timeRange, setTimeRange] = useState("All");
  const [filterProject, setFilterProject] = useState("");
  const [filterTeam, setFilterTeam] = useState("");

  // Team tab filters
  const [expandedId, setExpandedId] = useState(null);
  const [filterAssignee, setFilterAssignee] = useState("");
  const [teamStatusFilter, setTeamStatusFilter] = useState("Active");
  const [loadFilter, setLoadFilter] = useState("All");
  const [priChips, setPriChips] = useState([]);
  const [teamFilterProject, setTeamFilterProject] = useState("");
  const [teamFilterType, setTeamFilterType] = useState("");
  const [daysActiveFilter, setDaysActiveFilter] = useState("All");

  const [sprintStatusFilter, setSprintStatusFilter] = useState("All");

  // Weekly Trends
  const [weekTypeFilter, setWeekTypeFilter] = useState("All");

  const [weekendTypeFilter, setWeekendTypeFilter] = useState("All");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    axios.get(`${API_BASE_URL}/api/tasks`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setTasks(res.data.map(t => ({
        ...t,
        priority: normalizePriority(t.priority),
        status: normalizeStatus(t.status),
      }))))
      .catch(() => toast.error("Unable to load task data"))
      .finally(() => setLoading(false));
    axios.get(`${API_BASE_URL}/api/worklogs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setWorklogs(res.data))
      .catch(() => {}); // worklogs are supplementary — fail silently
  }, [navigate]);

  // Fetch sprints once tasks are loaded — one request per unique project
  useEffect(() => {
    if (sprintsFetched.current || tasks.length === 0) return;
    sprintsFetched.current = true;
    const token = localStorage.getItem("token");
    if (!token) return;
    const projectIds = [...new Set(tasks.filter(t => t.projectId).map(t => t.projectId))];
    if (projectIds.length === 0) return;
    Promise.all(
      projectIds.map(pid =>
        axios.get(`${API_BASE_URL}/api/sprints`, {
          params: { projectId: pid },
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.data).catch(() => [])
      )
    ).then(results => setSprints(results.flat()));
  }, [tasks]);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(link);
    return () => link.remove();
  }, []);

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  const projectOptions = useMemo(() => {
    const s = new Set(tasks.filter(t => t.projectName).map(t => t.projectName));
    return [{ value: "", label: "All Projects" }, ...[...s].sort().map(p => ({ value: p, label: p }))];
  }, [tasks]);

  const teamOptions = useMemo(() => {
    const s = new Set(tasks.filter(t => t.teamName).map(t => t.teamName));
    return [{ value: "", label: "All Teams" }, ...[...s].sort().map(t => ({ value: t, label: t }))];
  }, [tasks]);

  const typeOptions = useMemo(() => {
    const s = new Set(tasks.filter(t => t.taskType).map(t => t.taskType));
    return [{ value: "", label: "All Types" }, ...[...s].sort().map(t => ({ value: t, label: t }))];
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    let d = tasks;
    if (timeRange !== "All") {
      const cut = new Date(today); cut.setDate(cut.getDate() - TIME_DAYS[timeRange]);
      d = d.filter(t => t.createdAt && new Date(t.createdAt) >= cut);
    }
    if (filterProject) d = d.filter(t => t.projectName === filterProject);
    if (filterTeam) d = d.filter(t => t.teamName === filterTeam);
    return d;
  }, [tasks, timeRange, filterProject, filterTeam, today]);

  // All derived stats
  const D = useMemo(() => {
    const T2 = filteredTasks;
    const total = T2.length;
    const doneList = T2.filter(t => t.status === "COMPLETED");
    const completed = doneList.length;
    const open = T2.filter(t => OPEN_STATUSES.includes(t.status)).length;
    const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
    const overdue = T2.filter(t => t.dueDate && OPEN_STATUSES.includes(t.status) && new Date(t.dueDate) < today).length;
    const unassigned = T2.filter(t => !t.assigneeId && OPEN_STATUSES.includes(t.status)).length;
    const highPri = T2.filter(t => t.priority === "High" && OPEN_STATUSES.includes(t.status)).length;
    const critical = T2.filter(t => t.priority === "High" && t.dueDate && OPEN_STATUSES.includes(t.status) && new Date(t.dueDate) < today).length;
    const tTasks = doneList.filter(t => t.totalTime != null);
    const avgTime = tTasks.length > 0 ? Math.round(tTasks.reduce((s, t) => s + t.totalTime, 0) / tTasks.length) : 0;

    const statusData = Object.entries(T2.reduce((a, t) => { a[t.status] = (a[t.status] || 0) + 1; return a; }, {})).map(([name, value]) => ({ name, value }));
    const priorityData = Object.entries(T2.reduce((a, t) => { const p = t.priority || "None"; a[p] = (a[p] || 0) + 1; return a; }, {})).map(([name, value]) => ({ name, value }));
    const typeData = Object.entries(T2.reduce((a, t) => { const tp = t.taskType || "General"; a[tp] = (a[tp] || 0) + 1; return a; }, {})).map(([name, value]) => ({ name, value }));

    const { buckets: waB, order: waO } = buildWeekBuckets(14);
    waO.forEach(w => { waB[w] = 0; });
    T2.forEach(t => { if (!t.createdAt) return; const lb = isoWeekLabel(new Date(t.createdAt)); if (lb in waB) waB[lb]++; });
    const weeklyData = waO.map(w => ({ week: w, tasks: waB[w] }));

    const { buckets: wtB, order: wtO } = buildWeekBuckets(12);
    const typeSet = new Set();
    wtO.forEach(w => { wtB[w] = {}; });
    T2.forEach(t => {
      if (!t.createdAt) return;
      const lb = isoWeekLabel(new Date(t.createdAt));
      if (!(lb in wtB)) return;
      const tp = t.taskType || "General"; typeSet.add(tp);
      wtB[lb][tp] = (wtB[lb][tp] || 0) + 1;
    });
    const weeklyByType = wtO.map(w => ({ week: w, ...wtB[w] }));
    const allTypes = [...typeSet];

    const { buckets: wpB, order: wpO } = buildWeekBuckets(12);
    wpO.forEach(w => { wpB[w] = { High: 0, Medium: 0, Low: 0 }; });
    T2.forEach(t => {
      if (!t.createdAt) return;
      const lb = isoWeekLabel(new Date(t.createdAt));
      if (!(lb in wpB)) return;
      const p = t.priority || "Low";
      if (wpB[lb][p] !== undefined) wpB[lb][p]++;
    });
    const weeklyByPriority = wpO.map(w => ({ week: w, ...wpB[w] }));

    const aMap = {};
    T2.forEach(t => {
      if (!t.assigneeId) return;
      if (!aMap[t.assigneeId]) aMap[t.assigneeId] = { name: [t.assigneeFirstName, t.assigneeLastName].filter(Boolean).join(" ") || "Unknown", open: 0 };
      if (OPEN_STATUSES.includes(t.status)) aMap[t.assigneeId].open++;
    });
    const topAssignees = Object.values(aMap).sort((a, b) => b.open - a.open).slice(0, 8);

    const ago30 = new Date(today); ago30.setDate(ago30.getDate() - 30);
    const agingCount = T2.filter(t => OPEN_STATUSES.includes(t.status) && t.createdAt && new Date(t.createdAt) < ago30).length;

    // SLA data
    const slaByProject = {};
    T2.forEach(t => {
      const proj = t.projectName || "Unassigned";
      if (!slaByProject[proj]) slaByProject[proj] = { project: proj, onTime: 0, breached: 0, total: 0 };
      slaByProject[proj].total++;
      if (OPEN_STATUSES.includes(t.status) && t.dueDate && new Date(t.dueDate) < today) slaByProject[proj].breached++;
      else slaByProject[proj].onTime++;
    });
    const slaData = Object.values(slaByProject)
      .map(p => ({ ...p, compliance: p.total > 0 ? Math.round((p.onTime / p.total) * 100) : 100 }))
      .sort((a, b) => a.compliance - b.compliance)
      .slice(0, 10);
    const overallSla = total > 0 ? Math.round(((total - overdue) / total) * 100) : 100;

    // Monthly comparison
    const monthMap = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const lbl = monthLabel(d);
      monthMap[lbl] = { month: lbl, created: 0, completed: 0, openAtEnd: 0 };
    }
    T2.forEach(t => {
      if (!t.createdAt) return;
      const d = new Date(t.createdAt);
      const lbl = monthLabel(d);
      if (monthMap[lbl]) {
        monthMap[lbl].created++;
        if (t.status === "COMPLETED") monthMap[lbl].completed++;
        if (OPEN_STATUSES.includes(t.status)) monthMap[lbl].openAtEnd++;
      }
    });
    const monthlyData = Object.values(monthMap);

    // ── Monthly by Organization (top 6 projects) ──
    const projCountM = {};
    T2.forEach(t => { const p = t.projectName || "Unassigned"; projCountM[p] = (projCountM[p] || 0) + 1; });
    const topProjectsM = Object.entries(projCountM).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([p]) => p);

    const monthMapProj = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const lbl = monthLabel(d);
      monthMapProj[lbl] = { month: lbl };
      topProjectsM.forEach(p => { monthMapProj[lbl][p] = 0; });
    }
    T2.forEach(t => {
      if (!t.createdAt) return;
      const lbl = monthLabel(new Date(t.createdAt));
      const proj = t.projectName || "Unassigned";
      if (monthMapProj[lbl] && topProjectsM.includes(proj)) monthMapProj[lbl][proj]++;
    });
    const monthlyByOrg = Object.values(monthMapProj);

    // ── Monthly by Category ──
    const typeSetM = new Set();
    T2.forEach(t => { if (t.taskType) typeSetM.add(t.taskType); });
    const allTypesM = [...typeSetM];

    const monthMapType = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const lbl = monthLabel(d);
      monthMapType[lbl] = { month: lbl };
      allTypesM.forEach(tp => { monthMapType[lbl][tp] = 0; });
    }
    T2.forEach(t => {
      if (!t.createdAt) return;
      const lbl = monthLabel(new Date(t.createdAt));
      const tp = t.taskType || "General";
      if (monthMapType[lbl]) monthMapType[lbl][tp] = (monthMapType[lbl][tp] || 0) + 1;
    });
    const monthlyByCategory = Object.values(monthMapType);

    // ── Monthly Avg First Response & Time to Done (by type) ──
    const monthTimeMap = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const lbl = monthLabel(d);
      monthTimeMap[lbl] = { month: lbl };
    }
    const timeTypesM = [...typeSetM].slice(0, 5);
    T2.forEach(t => {
      if (t.totalTime == null || !t.createdAt) return;
      const lbl = monthLabel(new Date(t.createdAt));
      if (!monthTimeMap[lbl]) return;
      const tp = t.taskType || "General";
      if (!monthTimeMap[lbl][tp]) monthTimeMap[lbl][tp] = { sumDone: 0, sumResp: 0, count: 0 };
      monthTimeMap[lbl][tp].sumDone += t.totalTime;
      monthTimeMap[lbl][tp].sumResp += t.totalTime * 0.3;
      monthTimeMap[lbl][tp].count++;
    });
    const monthlyAvgTimeDone = Object.values(monthTimeMap).map(row => {
      const r = { month: row.month };
      timeTypesM.forEach(tp => { r[tp] = row[tp] ? Math.round(row[tp].sumDone / row[tp].count) : null; });
      return r;
    });
    const monthlyAvgFirstResponse = Object.values(monthTimeMap).map(row => {
      const r = { month: row.month };
      timeTypesM.forEach(tp => { r[tp] = row[tp] ? Math.round(row[tp].sumResp / row[tp].count) : null; });
      return r;
    });

    // Weekend analysis
    const weekendCreated = T2.filter(t => { if (!t.createdAt) return false; const d = new Date(t.createdAt).getDay(); return d === 0 || d === 6; }).length;
    const weekdayCreated = T2.filter(t => { if (!t.createdAt) return false; const d = new Date(t.createdAt).getDay(); return d > 0 && d < 6; }).length;
    const weekendDue = T2.filter(t => { if (!t.dueDate) return false; const d = new Date(t.dueDate).getDay(); return d === 0 || d === 6; }).length;
    const weekendOverdue = T2.filter(t => { if (!t.dueDate) return false; const d = new Date(t.dueDate).getDay(); return (d === 0 || d === 6) && OPEN_STATUSES.includes(t.status) && new Date(t.dueDate) < today; }).length;
    const weekdayData = [
      { name: "Mon", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 1).length },
      { name: "Tue", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 2).length },
      { name: "Wed", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 3).length },
      { name: "Thu", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 4).length },
      { name: "Fri", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 5).length },
      { name: "Sat", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 6).length, weekend: true },
      { name: "Sun", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 0).length, weekend: true },
    ];

    // ── Weekend Analysis extras ──

    // Tickets by Hour of Day
    const hourData = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getUTCHours() === h).length,
    }));

    // Weekend/Weekday close rates
    const weekendDoneList = T2.filter(t => { if (!t.createdAt) return false; const d = new Date(t.createdAt).getDay(); return d === 0 || d === 6; });
    const weekdayDoneList = T2.filter(t => { if (!t.createdAt) return false; const d = new Date(t.createdAt).getDay(); return d > 0 && d < 6; });
    const weekendClosed = weekendDoneList.filter(t => t.status === "COMPLETED").length;
    const weekdayClosed = weekdayDoneList.filter(t => t.status === "COMPLETED").length;
    const weekendCloseRate = weekendDoneList.length > 0 ? Math.round((weekendClosed / weekendDoneList.length) * 100) : 0;
    const weekdayCloseRate = weekdayDoneList.length > 0 ? Math.round((weekdayClosed / weekdayDoneList.length) * 100) : 0;

    // FR & TTD compliance (tasks with totalTime within threshold — proxy: totalTime <= avgTime * 1.2)
    const frThreshold = avgTime > 0 ? avgTime * 0.3 * 1.2 : Infinity;
    const ttdThreshold = avgTime > 0 ? avgTime * 1.2 : Infinity;
    const weekendFRCompliant = weekendDoneList.filter(t => t.totalTime != null && t.totalTime * 0.3 <= frThreshold).length;
    const weekdayFRCompliant = weekdayDoneList.filter(t => t.totalTime != null && t.totalTime * 0.3 <= frThreshold).length;
    const weekendTTDCompliant = weekendDoneList.filter(t => t.totalTime != null && t.totalTime <= ttdThreshold).length;
    const weekdayTTDCompliant = weekdayDoneList.filter(t => t.totalTime != null && t.totalTime <= ttdThreshold).length;
    const weekendFRTotal = weekendDoneList.filter(t => t.totalTime != null).length;
    const weekdayFRTotal = weekdayDoneList.filter(t => t.totalTime != null).length;
    const weekendFRRate = weekendFRTotal > 0 ? Math.round((weekendFRCompliant / weekendFRTotal) * 100 * 10) / 10 : 0;
    const weekdayFRRate = weekdayFRTotal > 0 ? Math.round((weekdayFRCompliant / weekdayFRTotal) * 100 * 10) / 10 : 0;
    const weekendTTDRate = weekendFRTotal > 0 ? Math.round((weekendTTDCompliant / weekendFRTotal) * 100 * 10) / 10 : 0;
    const weekdayTTDRate = weekdayFRTotal > 0 ? Math.round((weekdayTTDCompliant / weekdayFRTotal) * 100 * 10) / 10 : 0;

    // Team data
    const tMap = {};
    T2.forEach(t => {
      if (!t.assigneeId) return;
      if (!tMap[t.assigneeId]) tMap[t.assigneeId] = {
        id: t.assigneeId,
        name: [t.assigneeFirstName, t.assigneeLastName].filter(Boolean).join(" ") || "Unknown",
        taskType: t.taskType || "General",
        open: 0, high: 0, medium: 0, low: 0,
        overdue: 0, critical: 0, completed: 0, maxAge: 0, completedTimes: [],
        firstSeen: t.createdAt ? new Date(t.createdAt) : today,
      };
      const m = tMap[t.assigneeId];
      if (t.createdAt && new Date(t.createdAt) < m.firstSeen) m.firstSeen = new Date(t.createdAt);
      if (OPEN_STATUSES.includes(t.status)) {
        m.open++;
        if (t.priority === "High") m.high++;
        else if (t.priority === "Medium") m.medium++;
        else m.low++;
        if (t.dueDate && new Date(t.dueDate) < today) { m.overdue++; if (t.priority === "High") m.critical++; }
        if (t.createdAt) m.maxAge = Math.max(m.maxAge, Math.floor((today - new Date(t.createdAt)) / 86400000));
      }
      if (t.status === "COMPLETED") { m.completed++; if (t.totalTime != null) m.completedTimes.push(t.totalTime); }
    });
    const teamData = Object.values(tMap).map(m => ({
      ...m,
      daysActive: Math.floor((today - m.firstSeen) / 86400000),
      loadScore: Math.round((m.high * 3 + m.medium * 1.5 + m.low * 0.5) * 10) / 10,
      avgTime: m.completedTimes.length > 0
        ? (m.completedTimes.reduce((s, v) => s + v, 0) / m.completedTimes.length).toFixed(1) + "h"
        : "—",
    })).sort((a, b) => b.open - a.open);

    // ── Weekly Trends extra data ──

    // Weekly incoming (created) vs closed (completed) — 26 weeks
    const { buckets: wicB, order: wicO } = buildWeekBuckets(26);
    wicO.forEach(w => { wicB[w] = { week: w, incoming: 0, closed: 0 }; });
    T2.forEach(t => {
      if (t.createdAt) { const lb = isoWeekLabel(new Date(t.createdAt)); if (lb in wicB) wicB[lb].incoming++; }
      if (t.status === "COMPLETED" && t.createdAt) { const lb = isoWeekLabel(new Date(t.createdAt)); if (lb in wicB) wicB[lb].closed++; }
    });
    const weeklyIncomingVsClosed = wicO.map(w => wicB[w]);

    // Weekly by team/project (top 6 projects by task count) — 26 weeks
    const projCount = {};
    T2.forEach(t => { const p = t.projectName || "Unassigned"; projCount[p] = (projCount[p] || 0) + 1; });
    const topProjects = Object.entries(projCount).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([p]) => p);
    const { buckets: wpjB, order: wpjO } = buildWeekBuckets(26);
    wpjO.forEach(w => { wpjB[w] = { week: w }; topProjects.forEach(p => { wpjB[w][p] = 0; }); });
    T2.forEach(t => {
      if (!t.createdAt) return;
      const lb = isoWeekLabel(new Date(t.createdAt));
      const proj = t.projectName || "Unassigned";
      if (lb in wpjB && topProjects.includes(proj)) wpjB[lb][proj]++;
    });
    const weeklyByProject = wpjO.map(w => wpjB[w]);

    // Running backlog (cumulative: incoming - closed each week)
    let cumBacklog = 0;
    const runningBacklog = weeklyIncomingVsClosed.map(w => {
      cumBacklog += (w.incoming - w.closed);
      return { week: w.week, backlog: Math.max(0, cumBacklog) };
    });

    // Avg totalTime by type per week (proxy for "Avg First Response Time")
    const typeTimeMap = {};
    T2.forEach(t => {
      if (t.totalTime == null || !t.taskType) return;
      if (!typeTimeMap[t.taskType]) typeTimeMap[t.taskType] = { total: 0, count: 0 };
      typeTimeMap[t.taskType].total += t.totalTime;
      typeTimeMap[t.taskType].count++;
    });
    // Weekly avg time to done by type (LineChart) — 12 weeks
    const { buckets: watB, order: watO } = buildWeekBuckets(12);
    watO.forEach(w => { watB[w] = { week: w }; });
    const timeTypeSet = new Set();
    T2.forEach(t => {
      if (t.totalTime == null || !t.createdAt) return;
      const lb = isoWeekLabel(new Date(t.createdAt));
      if (!(lb in watB)) return;
      const tp = t.taskType || "General"; timeTypeSet.add(tp);
      if (!watB[lb][tp]) watB[lb][tp] = { sum: 0, count: 0 };
      watB[lb][tp].sum += t.totalTime;
      watB[lb][tp].count += 1;
    });
    const timeTypes = [...timeTypeSet].slice(0, 5);
    const weeklyAvgTimeDone = watO.map(w => {
      const row = { week: w };
      timeTypes.forEach(tp => {
        row[tp] = watB[w][tp] ? Math.round(watB[w][tp].sum / watB[w][tp].count * 10) / 10 : null;
      });
      return row;
    });

    // Avg "first response" proxy — use priority High tasks' totalTime as a proxy per week
    const { buckets: warB, order: warO } = buildWeekBuckets(12);
    warO.forEach(w => { warB[w] = { week: w }; });
    T2.forEach(t => {
      if (t.totalTime == null || !t.createdAt) return;
      const lb = isoWeekLabel(new Date(t.createdAt));
      if (!(lb in warB)) return;
      const tp = t.taskType || "General";
      if (!warB[lb][tp]) warB[lb][tp] = { sum: 0, count: 0 };
      warB[lb][tp].sum += t.totalTime * 0.3; // proxy: 30% of total as "response"
      warB[lb][tp].count += 1;
    });
    const weeklyAvgResponseTime = warO.map(w => {
      const row = { week: w };
      timeTypes.forEach(tp => {
        row[tp] = warB[w][tp] ? Math.round(warB[w][tp].sum / warB[w][tp].count * 10) / 10 : null;
      });
      return row;
    });

    return {
      total, open, completed, rate, avgTime, overdue, unassigned, highPri, critical, agingCount,
      statusData, priorityData, typeData, weeklyData, weeklyByType, allTypes, weeklyByPriority,
      topAssignees, teamData, slaData, overallSla, monthlyData,
      weekendCreated, weekdayCreated, weekendDue, weekendOverdue, weekdayData,
      weeklyIncomingVsClosed, weeklyByProject, topProjects, runningBacklog,
      weeklyAvgTimeDone, weeklyAvgResponseTime, timeTypes, monthlyByOrg, topProjectsM, monthlyByCategory, allTypesM: allTypesM,
      monthlyAvgTimeDone, monthlyAvgFirstResponse, timeTypesM, hourData,
      weekendClosed, weekdayClosed, weekendCloseRate, weekdayCloseRate,
      weekendFRRate, weekdayFRRate, weekendTTDRate, weekdayTTDRate,
      weekendFRTotal, weekdayFRTotal,
      weekendDoneList, weekdayDoneList,
    };
  }, [filteredTasks, today]);

  // Team tab assignee options — filtered by selected project
  const assigneeOptions = useMemo(() => {
    let base = filteredTasks;
    if (teamFilterProject) base = base.filter(t => t.projectName === teamFilterProject);
    const seen = new Set();
    const opts = [];
    base.forEach(t => {
      if (t.assigneeId && !seen.has(t.assigneeId)) {
        seen.add(t.assigneeId);
        opts.push({
          value: String(t.assigneeId),
          label: [t.assigneeFirstName, t.assigneeLastName].filter(Boolean).join(" ") || "Unknown",
        });
      }
    });
    return [{ value: "", label: "Any Assignee" }, ...opts.sort((a, b) => a.label.localeCompare(b.label))];
  }, [filteredTasks, teamFilterProject]);

  // Compute firstSeen from ALL tasks (unfiltered) so daysActive is not
  // affected by the global time-range filter.
  const assigneeFirstSeen = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      if (!t.assigneeId || !t.createdAt) return;
      const d = new Date(t.createdAt);
      if (!map[t.assigneeId] || d < map[t.assigneeId]) map[t.assigneeId] = d;
    });
    return map;
  }, [tasks]);

  const filteredTeamData = useMemo(() => {
    const allMaxScore = D.teamData[0]?.loadScore || 1;
    let d = D.teamData.map(m => {
      const firstSeen = assigneeFirstSeen[m.id];
      const daysActive = firstSeen
        ? Math.max(0, Math.floor((today - firstSeen) / 86400000))
        : 0;
      return {
        ...m,
        daysActive,  // override with true all-time value
        loadPct: allMaxScore > 0 ? Math.round((m.loadScore / allMaxScore) * 100) : 0,
      };
    });
    if (teamFilterProject) d = d.filter(m => {
      const memberTasks = filteredTasks.filter(t => t.assigneeId === m.id);
      return memberTasks.some(t => t.projectName === teamFilterProject);
    });
    if (teamFilterType) d = d.filter(m => m.taskType === teamFilterType);
    if (filterAssignee) d = d.filter(m => String(m.id) === filterAssignee);
    if (priChips.length > 0) d = d.filter(m =>
      priChips.every(p => p === "High" ? m.high > 0 : p === "Medium" ? m.medium > 0 : m.low > 0)
    );
    if (loadFilter === "Heavy") d = d.filter(m => m.loadPct > 65);
    if (loadFilter === "Medium") d = d.filter(m => m.loadPct > 35 && m.loadPct <= 65);
    if (loadFilter === "Normal") d = d.filter(m => m.loadPct <= 35);
    if (daysActiveFilter === "< 30d")  d = d.filter(m => m.daysActive < 30);
    if (daysActiveFilter === "30-90d") d = d.filter(m => m.daysActive >= 30 && m.daysActive < 90);
    if (daysActiveFilter === "> 90d")  d = d.filter(m => m.daysActive >= 90);
    if (teamStatusFilter === "Active") d = d.filter(m => m.open > 0);
    return d;
  }, [D.teamData, assigneeFirstSeen, today, teamFilterProject, teamFilterType, filterAssignee, priChips, loadFilter, daysActiveFilter, teamStatusFilter, filteredTasks]);

  const maxLoadScore = D.teamData[0]?.loadScore || 1;

  // Aggregate worklog hours per assignee via taskId (avoids employeeId vs assigneeId mismatch)
  const worklogByAssignee = useMemo(() => {
    // Step 1: sum hours per taskId from worklogs
    const taskHoursMap = {};
    worklogs.forEach(w => {
      if (!w.taskId || !(w.hours > 0)) return;
      taskHoursMap[w.taskId] = (taskHoursMap[w.taskId] || 0) + w.hours;
    });
    // Step 2: walk filteredTasks to accumulate per assignee, respecting filters
    const map = {};
    filteredTasks.forEach(t => {
      if (!t.assigneeId || !taskHoursMap[t.id]) return;
      if (teamFilterProject && t.projectName !== teamFilterProject) return;
      if (teamFilterType    && t.taskType    !== teamFilterType)    return;
      if (!map[t.assigneeId]) map[t.assigneeId] = 0;
      map[t.assigneeId] += taskHoursMap[t.id];
    });
    Object.keys(map).forEach(k => { map[k] = Math.round(map[k] * 10) / 10; });
    return map;
  }, [worklogs, filteredTasks, teamFilterProject, teamFilterType]);

  const filteredWeeklyByType = useMemo(() => {
    if (weekTypeFilter === "All") return D.weeklyByType;
    return D.weeklyByType.map(w => ({ week: w.week, [weekTypeFilter]: w[weekTypeFilter] || 0 }));
  }, [D.weeklyByType, weekTypeFilter]);

  // ── Projects tab stats ──
  const projectStats = useMemo(() => {
    const map = {};
    filteredTasks.forEach(t => {
      const key = t.projectId || t.projectName || "Unassigned";
      if (!map[key]) map[key] = { id: t.projectId, name: t.projectName || "Unassigned", total: 0, open: 0, completed: 0, overdue: 0 };
      const m = map[key];
      m.total++;
      if (OPEN_STATUSES.includes(t.status)) {
        m.open++;
        if (t.dueDate && new Date(t.dueDate) < today) m.overdue++;
      }
      if (t.status === "COMPLETED") m.completed++;
    });
    return Object.values(map).map(m => ({
      ...m,
      rate: m.total > 0 ? Math.round((m.completed / m.total) * 100) : 0,
    })).sort((a, b) => b.total - a.total);
  }, [filteredTasks, today]);

  // ── Sprints tab stats — sourced directly from /api/sprints ──
  const sprintStats = useMemo(() => {
    const ORDER = { ACTIVE: 0, IN_PROGRESS: 0, PLANNING: 1, PLANNED: 1, COMPLETED: 2, CANCELLED: 3 };
    let s = sprints;
    if (filterProject) s = s.filter(sp => sp.projectName === filterProject);
    return s.map(sp => ({
      id:          sp.id,
      name:        sp.name || `Sprint ${sp.sprintNumber}`,
      status:      sp.status || "PLANNING",
      number:      sp.sprintNumber || 0,
      projectName: sp.projectName || "",
      goal:        sp.goal || "",
      startDate:   sp.startDate,
      endDate:     sp.endDate,
      total:       sp.totalTasks     || 0,
      completed:   sp.completedTasks || 0,
      open:        (sp.pendingTasks || 0) + (sp.inProgressTasks || 0),
      cancelled:   sp.cancelledTasks || 0,
      progress:    sp.totalTasks > 0 ? Math.round((sp.completedTasks / sp.totalTasks) * 100) : 0,
    })).sort((a, b) => {
      const oa = ORDER[a.status] ?? 3, ob = ORDER[b.status] ?? 3;
      return oa !== ob ? oa - ob : (b.number || 0) - (a.number || 0);
    });
  }, [sprints, filterProject]);

  const filteredSprintStats = useMemo(() => {
    if (sprintStatusFilter === "All") return sprintStats;
    if (sprintStatusFilter === "ACTIVE")   return sprintStats.filter(s => s.status === "ACTIVE" || s.status === "IN_PROGRESS");
    if (sprintStatusFilter === "PLANNING") return sprintStats.filter(s => s.status === "PLANNING" || s.status === "PLANNED");
    return sprintStats.filter(s => s.status === sprintStatusFilter);
  }, [sprintStats, sprintStatusFilter]);

  // ── Timesheets tab stats — respects global project/team/time filters ──
  const timesheetStats = useMemo(() => {
    // Build lookup maps from tasks
    const taskProjectMap = {};
    const taskTeamMap    = {};
    const empNameMap     = {};
    tasks.forEach(t => {
      if (t.id) {
        taskProjectMap[t.id] = t.projectName;
        taskTeamMap[t.id]    = t.teamName;
      }
      if (t.assigneeId && !empNameMap[t.assigneeId]) {
        empNameMap[t.assigneeId] =
          [t.assigneeFirstName, t.assigneeLastName].filter(Boolean).join(" ") || null;
      }
    });

    // Filter worklogs by global filters
    let wlogs = worklogs;
    if (timeRange !== "All") {
      const cut = new Date(today);
      cut.setDate(cut.getDate() - TIME_DAYS[timeRange]);
      wlogs = wlogs.filter(w => w.date && new Date(w.date) >= cut);
    }
    if (filterProject) wlogs = wlogs.filter(w => taskProjectMap[w.taskId] === filterProject);
    if (filterTeam)    wlogs = wlogs.filter(w => taskTeamMap[w.taskId]    === filterTeam);

    // Aggregate by employee
    const empMap = {};
    wlogs.forEach(w => {
      if (!w.employeeId || !(w.hours > 0)) return;
      const name = w.employeeName || empNameMap[w.employeeId] || `Employee #${w.employeeId}`;
      if (!empMap[w.employeeId]) empMap[w.employeeId] = { id: w.employeeId, name, total: 0, entries: 0 };
      empMap[w.employeeId].total += w.hours;
      empMap[w.employeeId].entries++;
    });
    const byEmployee = Object.values(empMap)
      .map(e => ({ ...e, total: Math.round(e.total * 10) / 10 }))
      .sort((a, b) => b.total - a.total);

    // Weekly hours (last 12 weeks)
    const { buckets, order } = buildWeekBuckets(12);
    order.forEach(w => { buckets[w] = 0; });
    wlogs.forEach(w => {
      if (!w.hours || !w.date) return;
      const lb = isoWeekLabel(new Date(w.date));
      if (lb in buckets) buckets[lb] += w.hours;
    });
    const weeklyHours = order.map(w => ({ week: w, hours: Math.round(buckets[w] * 10) / 10 }));

    const totalHours  = Math.round(wlogs.reduce((s, w) => s + (w.hours || 0), 0) * 10) / 10;
    const avgPerPerson = byEmployee.length > 0 ? Math.round((totalHours / byEmployee.length) * 10) / 10 : 0;
    const thisWeekLb  = isoWeekLabel(new Date());
    const thisWeekH   = Math.round((buckets[thisWeekLb] || 0) * 10) / 10;

    return { byEmployee, weeklyHours, totalHours, avgPerPerson, thisWeekH };
  }, [worklogs, tasks, timeRange, filterProject, filterTeam, today]);

  // Weekend Analysis stats — re-derived whenever type filter changes
  const WD = useMemo(() => {
    let T2 = filteredTasks;
    if (weekendTypeFilter !== "All") T2 = T2.filter(t => t.taskType === weekendTypeFilter);

    const total = T2.length;
    const doneList = T2.filter(t => t.status === "COMPLETED");
    const tTasks = doneList.filter(t => t.totalTime != null);
    const avgTime = tTasks.length > 0 ? Math.round(tTasks.reduce((s, t) => s + t.totalTime, 0) / tTasks.length) : 0;

    const isWeekend = d => d === 0 || d === 6;
    const isWeekday = d => d > 0 && d < 6;

    const weekendCreated  = T2.filter(t => t.createdAt && isWeekend(new Date(t.createdAt).getDay())).length;
    const weekdayCreated  = T2.filter(t => t.createdAt && isWeekday(new Date(t.createdAt).getDay())).length;
    const weekendOverdue  = T2.filter(t => t.dueDate && isWeekend(new Date(t.dueDate).getDay()) && OPEN_STATUSES.includes(t.status) && new Date(t.dueDate) < today).length;

    const weekdayData = [
      { name: "Mon", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 1).length },
      { name: "Tue", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 2).length },
      { name: "Wed", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 3).length },
      { name: "Thu", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 4).length },
      { name: "Fri", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 5).length },
      { name: "Sat", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 6).length, weekend: true },
      { name: "Sun", tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getDay() === 0).length, weekend: true },
    ];

    const hourData = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      tasks: T2.filter(t => t.createdAt && new Date(t.createdAt).getUTCHours() === h).length,
    }));

    const weekendList = T2.filter(t => t.createdAt && isWeekend(new Date(t.createdAt).getDay()));
    const weekdayList = T2.filter(t => t.createdAt && isWeekday(new Date(t.createdAt).getDay()));
    const weekendClosed    = weekendList.filter(t => t.status === "COMPLETED").length;
    const weekdayClosed    = weekdayList.filter(t => t.status === "COMPLETED").length;
    const weekendCloseRate = weekendList.length > 0 ? Math.round((weekendClosed / weekendList.length) * 100) : 0;
    const weekdayCloseRate = weekdayList.length > 0 ? Math.round((weekdayClosed / weekdayList.length) * 100) : 0;

    const frThreshold  = avgTime > 0 ? avgTime * 0.3 * 1.2 : Infinity;
    const ttdThreshold = avgTime > 0 ? avgTime * 1.2       : Infinity;

    const weFRTotal  = weekendList.filter(t => t.totalTime != null).length;
    const wdFRTotal  = weekdayList.filter(t => t.totalTime != null).length;
    const weFRComp   = weekendList.filter(t => t.totalTime != null && t.totalTime * 0.3 <= frThreshold).length;
    const wdFRComp   = weekdayList.filter(t => t.totalTime != null && t.totalTime * 0.3 <= frThreshold).length;
    const weTTDComp  = weekendList.filter(t => t.totalTime != null && t.totalTime <= ttdThreshold).length;
    const wdTTDComp  = weekdayList.filter(t => t.totalTime != null && t.totalTime <= ttdThreshold).length;

    return {
      total, weekendCreated, weekdayCreated, weekendOverdue,
      weekdayData, hourData,
      weekendClosed, weekdayClosed, weekendCloseRate, weekdayCloseRate,
      weekendFRRate:  weFRTotal > 0 ? Math.round((weFRComp  / weFRTotal) * 1000) / 10 : 0,
      weekdayFRRate:  wdFRTotal > 0 ? Math.round((wdFRComp  / wdFRTotal) * 1000) / 10 : 0,
      weekendTTDRate: weFRTotal > 0 ? Math.round((weTTDComp / weFRTotal) * 1000) / 10 : 0,
      weekdayTTDRate: wdFRTotal > 0 ? Math.round((wdTTDComp / wdFRTotal) * 1000) / 10 : 0,
      weekendFRTotal: weFRTotal, weekdayFRTotal: wdFRTotal,
    };
  }, [filteredTasks, weekendTypeFilter, today]);

  const getAssigneeTasks = useCallback((id) => {
    let base = filteredTasks.filter(t => t.assigneeId === id && OPEN_STATUSES.includes(t.status));
    if (teamFilterProject) base = base.filter(t => t.projectName === teamFilterProject);
    if (teamFilterType)    base = base.filter(t => t.taskType === teamFilterType);
    return base.sort((a, b) => {
      const po = { High: 0, Medium: 1, Low: 2 };
      const diff = (po[a.priority] ?? 3) - (po[b.priority] ?? 3);
      return diff !== 0 ? diff : (!a.dueDate ? 1 : !b.dueDate ? -1 : new Date(a.dueDate) - new Date(b.dueDate));
    });
  }, [filteredTasks, teamFilterProject, teamFilterType]);

  const statCards = [
    { label: "Total Tasks", value: D.total, sub: "All tasks in system", accent: "#475569", Icon: FaTasks },
    { label: "Open", value: D.open, sub: "Currently active", accent: "#ea580c", Icon: FaClipboardCheck },
    { label: "Completed", value: D.completed, sub: `${D.rate}% completion rate`, accent: "#0284c7", Icon: FaCheckCircle },
    { label: "Avg Time", value: `${D.avgTime}h`, sub: "Mean time to resolve", accent: "#7c3aed", Icon: FaClock },
    { label: "Overdue", value: D.overdue, sub: "Past due date", accent: "#dc2626", Icon: FaExclamationTriangle },
    { label: "Unassigned", value: D.unassigned, sub: "Need an owner", accent: "#d97706", Icon: FaUserSlash },
    { label: "High Priority", value: D.highPri, sub: "Open & high priority", accent: "#dc2626", Icon: FaFire },
    { label: "Critical", value: D.critical, sub: "Overdue + high priority", accent: "#9f1239", Icon: FaBolt },
  ];

  const axisProps = { axisLine: false, tickLine: false };
  const xTick = { fontSize: 10, fill: T.text3 };
  const yTick = { fontSize: 10, fill: T.text3 };

  const anyGlobalFilter = timeRange !== "All" || filterProject || filterTeam;
  const anyTeamFilter = filterAssignee || teamStatusFilter !== "Active" || loadFilter !== "All" || priChips.length > 0 || teamFilterProject || teamFilterType || daysActiveFilter !== "All";

  const donutChart = ({ label, data, colorFn }) => (
    <div key={label} className="col-12 col-md-4">
      <Card style={{ height: "100%" }}>
        <CardHead label={label} />
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="38%" innerRadius={48} outerRadius={76} paddingAngle={3}>
              {data.map((e, i) => <Cell key={i} fill={colorFn(e, i)} />)}
            </Pie>
            <Tooltip {...ttStyle} />
            <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={8}
              wrapperStyle={{ fontSize: "0.72rem", lineHeight: "1.9", paddingLeft: 8, color: T.text2 }} />
          </PieChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );

  // ── ADMIN NAVBAR height offset ──
  // AdminNavbar is fixed at top (Bootstrap fixed-top). We assume ~64px height.
  // The sticky sub-nav sits below it, so we give the page a paddingTop to
  // push content below the stacked fixed + sticky headers.
  const NAVBAR_H = 108;   // height of AdminNavbar (adjust if different)
  const SUBNAV_H = 44;   // dark app sub-nav row height
  const FILTERBAR_H = 44;   // filter/time row height
  const DASHTABS_H = 40;   // dashboard sub-tabs row height

  const totalStickyH = NAVBAR_H + SUBNAV_H + FILTERBAR_H + (activeTopTab === "Dashboard" ? DASHTABS_H : 0);

  return (
    <>
      {/* Fixed AdminNavbar (already in place) */}
      <div className="fixed-top"><AdminNavbar /></div>

      {/* Full-height flex column — only content area scrolls */}
      <div style={{ paddingTop: NAVBAR_H, height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* ════ DARK APP SUB-NAV ════ */}
      <div style={{
        flexShrink: 0,
        background: T.white,
        borderBottom: `1px solid ${T.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 24px",
        height: SUBNAV_H,
      }}>
        {/* Left: top-level tabs */}
        <div className="d-flex align-items-center" style={{ gap: 2 }}>
          {TOP_TABS.map(tab => {
            const isActive = activeTopTab === tab;
            const Icon = TAB_ICONS[tab] || FaTh;
            return (
              <button
                key={tab}
                onClick={() => switchTopTab(tab)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "0 14px", height: SUBNAV_H,
                  fontSize: "0.8rem", fontWeight: isActive ? 700 : 500,
                  color: isActive ? T.primary : T.text2,
                  background: isActive ? "#eef2ff" : "transparent",
                  border: "none",
                  borderBottom: `2px solid ${isActive ? T.primary : "transparent"}`,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                <Icon size={12} />
                {tab}
              </button>
            );
          })}
        </div>

        {/* Right: action buttons */}
        <div className="d-flex align-items-center gap-2">
          <Link to="/adminviewtasks"
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 6,
              fontSize: "0.75rem", fontWeight: 600,
              background: T.primary, color: "#fff",
              textDecoration: "none", whiteSpace: "nowrap",
              transition: "opacity 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            <FaClipboardCheck size={11} /> Tasks Board
          </Link>
          <Link to="/sprints"
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "5px 14px", borderRadius: 6,
              fontSize: "0.75rem", fontWeight: 600,
              background: "transparent",
              color: "#475569",
              border: `1.5px solid ${T.border}`,
              textDecoration: "none", whiteSpace: "nowrap",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.text2; e.currentTarget.style.color = T.text1; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = "#475569"; }}
          >
            <BsLightningChargeFill size={11} /> Sprints Board
          </Link>
        </div>
      </div>

      {/* ════ WHITE FILTER BAR + TABS ════ */}
      <div style={{
        flexShrink: 0,
        background: T.white,
        borderBottom: `1px solid ${T.border}`,
        boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
      }}>
        <div style={{ padding: "0 24px" }}>

          {/* ── DASHBOARD filter bar ── */}
          {activeTopTab === "Dashboard" && (
            <>
              {/* Row 1: label + filters + time range */}
              <div className="d-flex align-items-center justify-content-between" style={{ height: FILTERBAR_H, gap: 12 }}>
                {/* Left */}
                <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                  <span style={{
                    fontSize: "0.68rem", fontWeight: 800, color: T.text3,
                    textTransform: "uppercase", letterSpacing: "0.1em", marginRight: 6,
                  }}>
                    Dashboard
                  </span>
                  {projectOptions.length > 1 && (
                    <PillSelect value={filterProject} onChange={setFilterProject} options={projectOptions} active={!!filterProject} />
                  )}
                  {teamOptions.length > 1 && (
                    <PillSelect value={filterTeam} onChange={setFilterTeam} options={teamOptions} active={!!filterTeam} />
                  )}
                  {anyGlobalFilter && (
                    <button onClick={() => { setTimeRange("All"); setFilterProject(""); setFilterTeam(""); }}
                      style={{ fontSize: "0.72rem", color: T.text3, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                      Reset
                    </button>
                  )}
                </div>

                {/* Right: time range */}
                <div style={{ display: "inline-flex", border: `1.5px solid ${T.border}`, borderRadius: 6, overflow: "hidden", flexShrink: 0 }}>
                  {TIME_OPTS.map((opt, i) => (
                    <button key={opt} onClick={() => setTimeRange(opt)} style={{
                      padding: "4px 12px", fontSize: "0.75rem",
                      border: "none",
                      borderLeft: i > 0 ? `1px solid ${T.border}` : "none",
                      background: timeRange === opt ? "#0f172a" : "transparent",
                      color: timeRange === opt ? "#fff" : T.text3,
                      cursor: "pointer", fontWeight: timeRange === opt ? 700 : 500,
                      transition: "all 0.12s",
                    }}>{opt}</button>
                  ))}
                </div>
              </div>

              {/* Row 2: dashboard sub-tabs */}
              <div className="d-flex" style={{ gap: 0, borderTop: `1px solid ${T.border}` }}>
                {DASH_TABS.map(tab => {
                  const isActive = activeDashTab === tab;
                  return (
                    <button key={tab} onClick={() => switchDashTab(tab)} style={{
                      padding: "0 18px", height: DASHTABS_H,
                      fontSize: "0.82rem",
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? T.primary : T.text2,
                      borderBottom: `2px solid ${isActive ? T.primary : "transparent"}`,
                      background: "transparent", border: "none",
                      borderBottomWidth: 2,
                      borderBottomStyle: "solid",
                      borderBottomColor: isActive ? T.primary : "transparent",
                      cursor: "pointer", whiteSpace: "nowrap",
                      transition: "color 0.15s, border-color 0.15s",
                    }}>
                      {tab}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ── PROJECTS filter bar ── */}
          {activeTopTab === "Projects" && (
            <div className="d-flex align-items-center gap-3" style={{ height: FILTERBAR_H }}>
              <span style={{ fontSize: "0.68rem", fontWeight: 800, color: T.text3, textTransform: "uppercase", letterSpacing: "0.1em" }}>Projects</span>
              <div style={{ width: 1, height: 18, background: T.border }} />
              <PillSelect value={filterProject} onChange={setFilterProject} options={projectOptions} active={!!filterProject} />
              <PillSelect value={filterTeam} onChange={setFilterTeam} options={teamOptions} active={!!filterTeam} />
              <div style={{ display: "inline-flex", border: `1.5px solid ${T.border}`, borderRadius: 6, overflow: "hidden", marginLeft: "auto" }}>
                {TIME_OPTS.map((opt, i) => (
                  <button key={opt} onClick={() => setTimeRange(opt)} style={{ padding: "4px 12px", fontSize: "0.75rem", border: "none", borderLeft: i > 0 ? `1px solid ${T.border}` : "none", background: timeRange === opt ? "#0f172a" : "transparent", color: timeRange === opt ? "#fff" : T.text3, cursor: "pointer", fontWeight: timeRange === opt ? 700 : 500, transition: "all 0.12s" }}>{opt}</button>
                ))}
              </div>
            </div>
          )}

          {/* ── SPRINTS filter bar ── */}
          {activeTopTab === "Sprints" && (
            <div className="d-flex align-items-center gap-3" style={{ height: FILTERBAR_H }}>
              <span style={{ fontSize: "0.68rem", fontWeight: 800, color: T.text3, textTransform: "uppercase", letterSpacing: "0.1em" }}>Sprints</span>
              <div style={{ width: 1, height: 18, background: T.border }} />
              <PillSelect value={filterProject} onChange={setFilterProject} options={projectOptions} active={!!filterProject} />
              <div style={{ display: "inline-flex", border: `1.5px solid ${T.border}`, borderRadius: 6, overflow: "hidden" }}>
                {["All", "ACTIVE", "PLANNING", "COMPLETED", "CANCELLED"].map((opt, i) => (
                  <button key={opt} onClick={() => setSprintStatusFilter(opt)} style={{ padding: "4px 12px", fontSize: "0.75rem", border: "none", borderLeft: i > 0 ? `1px solid ${T.border}` : "none", background: sprintStatusFilter === opt ? T.primary : "transparent", color: sprintStatusFilter === opt ? "#fff" : T.text2, cursor: "pointer", fontWeight: sprintStatusFilter === opt ? 700 : 500, transition: "all 0.12s", whiteSpace: "nowrap" }}>{opt === "All" ? "All" : opt.charAt(0) + opt.slice(1).toLowerCase()}</button>
                ))}
              </div>
            </div>
          )}

          {/* ── TIMESHEETS filter bar ── */}
          {activeTopTab === "Timesheets" && (
            <div className="d-flex align-items-center gap-3" style={{ height: FILTERBAR_H, flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.68rem", fontWeight: 800, color: T.text3, textTransform: "uppercase", letterSpacing: "0.1em" }}>Timesheets</span>
              <div style={{ width: 1, height: 18, background: T.border }} />
              {projectOptions.length > 1 && (
                <PillSelect value={filterProject} onChange={setFilterProject} options={projectOptions} active={!!filterProject} />
              )}
              {teamOptions.length > 1 && (
                <PillSelect value={filterTeam} onChange={setFilterTeam} options={teamOptions} active={!!filterTeam} />
              )}
              {(filterProject || filterTeam || timeRange !== "All") && (
                <button onClick={() => { setFilterProject(""); setFilterTeam(""); setTimeRange("All"); }}
                  style={{ fontSize: "0.72rem", color: T.text3, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                  Reset
                </button>
              )}
              <div style={{ display: "inline-flex", border: `1.5px solid ${T.border}`, borderRadius: 6, overflow: "hidden", marginLeft: "auto" }}>
                {TIME_OPTS.map((opt, i) => (
                  <button key={opt} onClick={() => setTimeRange(opt)} style={{ padding: "4px 12px", fontSize: "0.75rem", border: "none", borderLeft: i > 0 ? `1px solid ${T.border}` : "none", background: timeRange === opt ? "#0f172a" : "transparent", color: timeRange === opt ? "#fff" : T.text3, cursor: "pointer", fontWeight: timeRange === opt ? 700 : 500, transition: "all 0.12s" }}>{opt}</button>
                ))}
              </div>
            </div>
          )}

          {/* ── REPORTS filter bar ── */}
          {activeTopTab === "Reports" && (
            <div className="d-flex align-items-center gap-3" style={{ height: FILTERBAR_H }}>
              <span style={{ fontSize: "0.68rem", fontWeight: 800, color: T.text3, textTransform: "uppercase", letterSpacing: "0.1em" }}>Reports</span>
              <div style={{ width: 1, height: 18, background: T.border }} />
              <PillSelect value={filterProject} onChange={setFilterProject} options={projectOptions} active={!!filterProject} />
              <PillSelect value={filterTeam} onChange={setFilterTeam} options={teamOptions} active={!!filterTeam} />
              <div style={{ display: "inline-flex", border: `1.5px solid ${T.border}`, borderRadius: 6, overflow: "hidden", marginLeft: "auto" }}>
                {TIME_OPTS.map((opt, i) => (
                  <button key={opt} onClick={() => setTimeRange(opt)} style={{ padding: "4px 12px", fontSize: "0.75rem", border: "none", borderLeft: i > 0 ? `1px solid ${T.border}` : "none", background: timeRange === opt ? "#0f172a" : "transparent", color: timeRange === opt ? "#fff" : T.text3, cursor: "pointer", fontWeight: timeRange === opt ? 700 : 500 }}>{opt}</button>
                ))}
              </div>
            </div>
          )}

          {/* ── TEAM filter bar ── */}
          {activeTopTab === "Team" && (
            <div className="d-flex align-items-center gap-3" style={{ padding: "10px 0", flexWrap: "wrap" }}>
              <span style={{
                fontSize: "0.68rem", fontWeight: 800, color: T.text3,
                textTransform: "uppercase", letterSpacing: "0.1em",
              }}>
                Team Management
              </span>
              <div style={{ width: 1, height: 18, background: T.border }} />

              {projectOptions.length > 1 && (
                <PillSelect value={teamFilterProject} onChange={v => { setTeamFilterProject(v); setFilterAssignee(""); }} options={projectOptions} active={!!teamFilterProject} />
              )}
              <PillSelect value={filterAssignee} onChange={setFilterAssignee} options={assigneeOptions} active={!!filterAssignee} />
              {typeOptions.length > 1 && (
                <PillSelect value={teamFilterType} onChange={setTeamFilterType} options={typeOptions} active={!!teamFilterType} />
              )}

              <div style={{ width: 1, height: 18, background: T.border }} />

              {/* Days Active filter */}
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: "0.68rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.06em" }}>Days Active:</span>
                <div style={{ display: "inline-flex", border: `1.5px solid ${T.border}`, borderRadius: 6, overflow: "hidden" }}>
                  {["All", "< 30d", "30-90d", "> 90d"].map((opt, i) => (
                    <button key={opt} onClick={() => setDaysActiveFilter(opt)} style={{
                      padding: "3px 10px", fontSize: "0.72rem", border: "none",
                      borderLeft: i > 0 ? `1px solid ${T.border}` : "none",
                      background: daysActiveFilter === opt ? T.primary : "transparent",
                      color: daysActiveFilter === opt ? T.white : T.text2,
                      cursor: "pointer", fontWeight: daysActiveFilter === opt ? 700 : 500,
                      transition: "all 0.12s", whiteSpace: "nowrap",
                    }}>{opt}</button>
                  ))}
                </div>
              </div>

              <div style={{ width: 1, height: 18, background: T.border }} />

              <ToggleGroup options={["Active", "All"]} value={teamStatusFilter} onChange={setTeamStatusFilter} />

              {anyTeamFilter && (
                <button onClick={() => { setFilterAssignee(""); setTeamStatusFilter("Active"); setLoadFilter("All"); setPriChips([]); setTeamFilterProject(""); setTeamFilterType(""); setDaysActiveFilter("All"); }}
                  style={{ fontSize: "0.72rem", color: T.text3, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0, marginLeft: "auto" }}>
                  Reset all
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ════ MAIN CONTENT — scrolls inside flex column ════ */}
      <div className="cb-scroll" style={{ flex: 1, overflowY: "auto", background: T.bg }}>

        {/* ── Full-area spinner while data loads or tab switches ── */}
        {(loading || tabSwitching) ? (
          <div style={{
            height: "100%", minHeight: 420,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 16,
          }}>
            <div style={{ position: "relative", width: 48, height: 48 }}>
              <div className="spinner-border" style={{ width: 48, height: 48, color: T.primary, opacity: 0.18, position: "absolute", top: 0, left: 0 }} />
              <div className="spinner-border" style={{ width: 48, height: 48, color: T.primary, position: "absolute", top: 0, left: 0, borderTopColor: "transparent", borderRightColor: "transparent" }} />
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={{ fontSize: "0.92rem", fontWeight: 600, color: T.text1, marginBottom: 4 }}>
                {loading
                  ? `Loading ${activeTopTab === "Team" ? "Team Management" : activeDashTab}`
                  : `Opening ${activeTopTab === "Team" ? "Team Management" : activeDashTab}`}
              </p>
              <p style={{ fontSize: "0.78rem", color: T.text3, marginBottom: 0 }}>
                {loading ? "Fetching tasks and worklog data…" : "Preparing view…"}
              </p>
            </div>
          </div>
        ) : (
        <div style={{ padding: "20px 24px" }}>

          {/* Active filter pills */}
          {anyGlobalFilter && activeTopTab === "Dashboard" && (
            <div className="d-flex gap-2 flex-wrap mb-3">
              {timeRange !== "All" && (
                <span style={{ fontSize: "0.72rem", background: "#eef2ff", color: T.primary, borderRadius: 20, padding: "3px 12px", fontWeight: 600, border: `1px solid #c7d2fe` }}>
                  Last {timeRange}
                </span>
              )}
              {filterProject && (
                <span style={{ fontSize: "0.72rem", background: "#eef2ff", color: T.primary, borderRadius: 20, padding: "3px 12px", fontWeight: 600, border: `1px solid #c7d2fe` }}>
                  {filterProject}
                </span>
              )}
              {filterTeam && (
                <span style={{ fontSize: "0.72rem", background: "#eef2ff", color: T.primary, borderRadius: 20, padding: "3px 12px", fontWeight: 600, border: `1px solid #c7d2fe` }}>
                  {filterTeam}
                </span>
              )}
              <span style={{ fontSize: "0.72rem", color: T.text3, padding: "3px 0" }}>
                {filteredTasks.length} of {tasks.length} tasks
              </span>
            </div>
          )}

              {/* ══════════════ TOP TAB: DASHBOARD ══════════════ */}
              {activeTopTab === "Dashboard" && (
                <>
                  {/* ── SUB TAB: OVERVIEW ── */}
                  {activeDashTab === "Overview" && (
                    <>
                      <div className="row g-3 mb-4">
                        {statCards.map((c, i) => (
                          <div key={i} className="col-6 col-md-3"><StatCard {...c} /></div>
                        ))}
                      </div>

                      <Card style={{ marginBottom: 20 }}>
                        <CardHead label="Task Volume by Week"
                          right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>{filteredTasks.length} tasks · {D.weeklyData.length} weeks</span>}
                        />
                        <ResponsiveContainer width="100%" height={160}>
                          <AreaChart data={D.weeklyData} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                            <defs>
                              <linearGradient id="ag1" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={T.primary} stopOpacity={0.15} />
                                <stop offset="95%" stopColor={T.primary} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                            <XAxis dataKey="week" tick={xTick} {...axisProps} />
                            <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                            <Tooltip {...ttStyle} />
                            <Area type="monotone" dataKey="tasks" name="Tasks Created" stroke={T.primary} strokeWidth={2}
                              fill="url(#ag1)" dot={{ fill: T.primary, r: 3, strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 0 }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Card>

                      <div className="row g-3 mb-4">
                        {[
                          { label: "By Status", data: D.statusData, colorFn: (e) => STATUS_COLORS[e.name] || "#94a3b8" },
                          { label: "By Priority", data: D.priorityData, colorFn: (e) => PRIORITY_COLORS[e.name] || "#94a3b8" },
                          { label: "By Task Type", data: D.typeData, colorFn: (_, i) => TYPE_COLORS[i % TYPE_COLORS.length] },
                        ].map(donutChart)}
                      </div>

                      <div className="row g-3">
                        <div className="col-12 col-md-7">
                          <Card style={{ height: "100%" }}>
                            <CardHead label="Top Assignees"
                              right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>{D.topAssignees.length} assignees</span>}
                            />
                            {D.topAssignees.length === 0
                              ? <p style={{ fontSize: "0.84rem", color: T.text3 }}>No assigned open tasks.</p>
                              : <ResponsiveContainer width="100%" height={Math.max(D.topAssignees.length * 38 + 16, 120)}>
                                <BarChart data={D.topAssignees} layout="vertical" margin={{ top: 0, right: 52, left: 4, bottom: 0 }} barSize={13}>
                                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={T.border} />
                                  <XAxis type="number" allowDecimals={false} tick={xTick} {...axisProps} />
                                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12, fill: T.text2 }} {...axisProps} />
                                  <Tooltip {...ttStyle} cursor={{ fill: T.bg }} />
                                  <Bar dataKey="open" name="Open Tasks" fill={T.primary} radius={[0, 6, 6, 0]}
                                    label={{ position: "right", fontSize: 12, fill: T.text2, fontWeight: 700 }} />
                                </BarChart>
                              </ResponsiveContainer>
                            }
                          </Card>
                        </div>
                        <div className="col-12 col-md-5">
                          <Card style={{ height: "100%" }}>
                            <CardHead label="Aging Tasks › 30 Days" />
                            {D.agingCount === 0
                              ? <p style={{ fontSize: "0.84rem", color: T.text3, lineHeight: 1.7 }}>All open tasks created within last 30 days. No action needed.</p>
                              : <>
                                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
                                  <span style={{ fontSize: "2.5rem", fontWeight: 800, color: "#dc2626", lineHeight: 1 }}>{D.agingCount}</span>
                                  <span style={{ fontSize: "0.84rem", color: T.text2 }}>open {D.agingCount === 1 ? "task" : "tasks"} unresolved &gt; 30 days</span>
                                </div>
                                <p style={{ fontSize: "0.82rem", color: T.text3, lineHeight: 1.6 }}>These tasks may be blocked or forgotten. Review and update their status.</p>
                              </>
                            }
                            <Link to="/adminviewtasks" style={{ fontSize: "0.78rem", color: T.primary, fontWeight: 600, textDecoration: "none" }}>
                              Review all tasks →
                            </Link>
                          </Card>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── SUB TAB: WEEKLY TRENDS ── */}
                  {activeDashTab === "Weekly Trends" && (() => {
                    const chartMargin = { top: 6, right: 10, left: -18, bottom: 0 };
                    const chartH = 240;
                    const legendStyle = { fontSize: "0.7rem", paddingTop: 8, color: T.text2 };
                    const projColors = ["#3b82f6", "#f97316", "#10b981", "#ec4899", "#f59e0b", "#8b5cf6"];
                    const lineColors = ["#3b82f6", "#ef4444", "#f97316", "#8b5cf6", "#10b981"];

                    return (
                      <>
                        {/* Header row: title + type filter pills */}
                        <div style={{ marginBottom: 16 }}>
                          <p style={{ fontSize: "0.72rem", color: T.text2, marginBottom: 10, fontWeight: 500 }}>
                            <strong style={{ color: T.text1 }}>{filteredTasks.length}</strong> tasks · <strong style={{ color: T.text1 }}>{D.weeklyIncomingVsClosed.length}</strong> weeks · split by category and project
                          </p>
                          <div className="d-flex flex-wrap gap-2">
                            {["All", ...D.allTypes].map(tp => (
                              <button key={tp} onClick={() => setWeekTypeFilter(tp)} style={{
                                padding: "4px 14px", fontSize: "0.78rem", borderRadius: 6, cursor: "pointer",
                                border: `1.5px solid ${weekTypeFilter === tp ? T.primary : T.border}`,
                                background: weekTypeFilter === tp ? T.primary : T.white,
                                color: weekTypeFilter === tp ? T.white : T.text2,
                                fontWeight: weekTypeFilter === tp ? 600 : 500,
                                transition: "all 0.15s",
                              }}>{tp}</button>
                            ))}
                          </div>
                        </div>

                        {/* 2-column grid of 6 charts */}
                        <div className="row g-3">

                          {/* 1. Weekly Volume — Incoming vs Closed */}
                          <div className="col-12 col-xl-6">
                            <Card>
                              <CardHead label="Weekly Volume (Incoming vs Closed)" />
                              <ResponsiveContainer width="100%" height={chartH}>
                                <BarChart data={D.weeklyIncomingVsClosed} margin={chartMargin} barSize={6} barGap={2}>
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                  <XAxis dataKey="week" tick={xTick} {...axisProps} interval={3} />
                                  <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                  <Tooltip {...ttStyle} />
                                  <Legend wrapperStyle={legendStyle} iconType="circle" iconSize={7} />
                                  <Bar dataKey="incoming" name="Incoming" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                                  <Bar dataKey="closed" name="Closed" fill="#10b981" radius={[2, 2, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </Card>
                          </div>

                          {/* 2. Weekly Category Breakdown */}
                          <div className="col-12 col-xl-6">
                            <Card>
                              <CardHead label="Weekly Category Breakdown" />
                              <ResponsiveContainer width="100%" height={chartH}>
                                <BarChart
                                  data={(weekTypeFilter === "All" ? D.weeklyByType : D.weeklyByType.map(w => ({ week: w.week, [weekTypeFilter]: w[weekTypeFilter] || 0 })))}
                                  margin={chartMargin} barSize={6}
                                >
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                  <XAxis dataKey="week" tick={xTick} {...axisProps} interval={3} />
                                  <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                  <Tooltip {...ttStyle} />
                                  <Legend wrapperStyle={legendStyle} iconType="circle" iconSize={7} />
                                  {(weekTypeFilter === "All" ? D.allTypes : [weekTypeFilter]).map((tp, i) => (
                                    <Bar key={tp} dataKey={tp} stackId="a"
                                      fill={TYPE_COLORS[D.allTypes.indexOf(tp) % TYPE_COLORS.length]}
                                      radius={i === (weekTypeFilter === "All" ? D.allTypes : [weekTypeFilter]).length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]} />
                                  ))}
                                </BarChart>
                              </ResponsiveContainer>
                            </Card>
                          </div>

                          {/* 3. Weekly Volume by Project/Org */}
                          <div className="col-12 col-xl-6">
                            <Card>
                              <CardHead label="Weekly Volume by Project" />
                              <ResponsiveContainer width="100%" height={chartH}>
                                <BarChart data={D.weeklyByProject} margin={chartMargin} barSize={6}>
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                  <XAxis dataKey="week" tick={xTick} {...axisProps} interval={3} />
                                  <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                  <Tooltip {...ttStyle} />
                                  <Legend wrapperStyle={legendStyle} iconType="circle" iconSize={7} />
                                  {D.topProjects.map((proj, i) => (
                                    <Bar key={proj} dataKey={proj} stackId="b"
                                      fill={projColors[i % projColors.length]}
                                      radius={i === D.topProjects.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]} />
                                  ))}
                                </BarChart>
                              </ResponsiveContainer>
                            </Card>
                          </div>

                          {/* 4. Running Backlog (Cumulative Net) */}
                          <div className="col-12 col-xl-6">
                            <Card>
                              <CardHead label="Running Backlog (Cumulative Net)"
                                right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>net open over time</span>}
                              />
                              <ResponsiveContainer width="100%" height={chartH}>
                                <AreaChart data={D.runningBacklog} margin={chartMargin}>
                                  <defs>
                                    <linearGradient id="backlogGrad" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.18} />
                                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02} />
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                  <XAxis dataKey="week" tick={xTick} {...axisProps} interval={3} />
                                  <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                  <Tooltip {...ttStyle} />
                                  <Area type="monotone" dataKey="backlog" name="Backlog" stroke="#6366f1" strokeWidth={2}
                                    fill="url(#backlogGrad)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                                </AreaChart>
                              </ResponsiveContainer>
                            </Card>
                          </div>

                          {/* 5. Avg First Response Time (by type) */}
                          <div className="col-12 col-xl-6">
                            <Card>
                              <CardHead label="Avg First Response Time (Hours)"
                                right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>by task type · 12 weeks</span>}
                              />
                              {D.timeTypes.length === 0
                                ? <p style={{ fontSize: "0.82rem", color: T.text3 }}>No time tracking data available.</p>
                                : (
                                  <ResponsiveContainer width="100%" height={chartH}>
                                    <LineChart data={D.weeklyAvgResponseTime} margin={chartMargin}>
                                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                      <XAxis dataKey="week" tick={xTick} {...axisProps} interval={2} />
                                      <YAxis tick={yTick} {...axisProps} />
                                      <Tooltip {...ttStyle} />
                                      <Legend wrapperStyle={legendStyle} iconType="circle" iconSize={7} />
                                      {D.timeTypes.map((tp, i) => (
                                        <Line key={tp} type="monotone" dataKey={tp}
                                          stroke={lineColors[i % lineColors.length]}
                                          strokeWidth={1.5} dot={false}
                                          activeDot={{ r: 4, strokeWidth: 0 }}
                                          connectNulls />
                                      ))}
                                    </LineChart>
                                  </ResponsiveContainer>
                                )}
                            </Card>
                          </div>

                          {/* 6. Avg Time to Done (by type) */}
                          <div className="col-12 col-xl-6">
                            <Card>
                              <CardHead label="Avg Time to Done (Hours)"
                                right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>by task type · 12 weeks</span>}
                              />
                              {D.timeTypes.length === 0
                                ? <p style={{ fontSize: "0.82rem", color: T.text3 }}>No time tracking data available.</p>
                                : (
                                  <ResponsiveContainer width="100%" height={chartH}>
                                    <LineChart data={D.weeklyAvgTimeDone} margin={chartMargin}>
                                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                      <XAxis dataKey="week" tick={xTick} {...axisProps} interval={2} />
                                      <YAxis tick={yTick} {...axisProps} />
                                      <Tooltip {...ttStyle} />
                                      <Legend wrapperStyle={legendStyle} iconType="circle" iconSize={7} />
                                      {D.timeTypes.map((tp, i) => (
                                        <Line key={tp} type="monotone" dataKey={tp}
                                          stroke={lineColors[i % lineColors.length]}
                                          strokeWidth={1.5} dot={false}
                                          activeDot={{ r: 4, strokeWidth: 0 }}
                                          connectNulls />
                                      ))}
                                    </LineChart>
                                  </ResponsiveContainer>
                                )}
                            </Card>
                          </div>

                        </div>
                      </>
                    );
                  })()}

                  {/* ── SUB TAB: SLA PERFORMANCE ── */}
                  {activeDashTab === "SLA Performance" && (
                    <>
                      {/* Summary row */}
                      <div className="row g-3 mb-4">
                        {[
                          { label: "Overall SLA", value: `${D.overallSla}%`, sub: "Tasks within SLA", accent: D.overallSla >= 80 ? "#10b981" : D.overallSla >= 60 ? "#f59e0b" : "#dc2626", Icon: FaCheckCircle },
                          { label: "SLA Breaches", value: D.overdue, sub: "Open & past due", accent: "#dc2626", Icon: FaExclamationTriangle },
                          { label: "Critical SLA", value: D.critical, sub: "High priority + overdue", accent: "#9f1239", Icon: FaBolt },
                          { label: "On-Time Tasks", value: D.total - D.overdue, sub: "Within SLA window", accent: "#0284c7", Icon: FaClipboardCheck },
                        ].map((c, i) => (
                          <div key={i} className="col-6 col-md-3"><StatCard {...c} /></div>
                        ))}
                      </div>

                      <div className="row g-3">
                        <div className="col-12 col-md-8">
                          <Card>
                            <CardHead label="SLA Compliance by Project"
                              right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>{D.slaData.length} projects</span>}
                            />
                            {D.slaData.length === 0
                              ? <p style={{ fontSize: "0.84rem", color: T.text3 }}>No project data available.</p>
                              : (
                                <>
                                  {D.slaData.map((p, i) => (
                                    <div key={i} style={{ marginBottom: 14 }}>
                                      <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: T.text1 }}>{p.project}</span>
                                        <div className="d-flex align-items-center gap-3">
                                          <span style={{ fontSize: "0.72rem", color: T.text3 }}>{p.onTime} on-time / {p.breached} breached</span>
                                          <span style={{ fontSize: "0.78rem", fontWeight: 700, color: p.compliance >= 80 ? "#10b981" : p.compliance >= 60 ? "#f59e0b" : "#dc2626" }}>
                                            {p.compliance}%
                                          </span>
                                        </div>
                                      </div>
                                      <div style={{ height: 7, background: T.border, borderRadius: 4, overflow: "hidden" }}>
                                        <div style={{
                                          height: "100%",
                                          width: `${p.compliance}%`,
                                          background: p.compliance >= 80 ? "#10b981" : p.compliance >= 60 ? "#f59e0b" : "#dc2626",
                                          borderRadius: 4,
                                          transition: "width 0.5s ease",
                                        }} />
                                      </div>
                                    </div>
                                  ))}
                                </>
                              )
                            }
                          </Card>
                        </div>
                        <div className="col-12 col-md-4">
                          <Card style={{ height: "100%" }}>
                            <CardHead label="SLA Breakdown" />
                            <ResponsiveContainer width="100%" height={220}>
                              <PieChart>
                                <Pie
                                  data={[
                                    { name: "On Time", value: D.total - D.overdue },
                                    { name: "Overdue", value: D.overdue },
                                  ]}
                                  dataKey="value" nameKey="name"
                                  cx="50%" cy="45%" innerRadius={50} outerRadius={78} paddingAngle={4}
                                >
                                  <Cell fill="#10b981" />
                                  <Cell fill="#ef4444" />
                                </Pie>
                                <Tooltip {...ttStyle} />
                                <Legend wrapperStyle={{ fontSize: "0.72rem", color: T.text2 }} iconType="circle" iconSize={8} />
                              </PieChart>
                            </ResponsiveContainer>
                            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12, marginTop: 4 }}>
                              <div className="d-flex justify-content-between">
                                <span style={{ fontSize: "0.72rem", color: T.text3 }}>Overall compliance</span>
                                <span style={{ fontSize: "0.84rem", fontWeight: 800, color: D.overallSla >= 80 ? "#10b981" : "#f59e0b" }}>{D.overallSla}%</span>
                              </div>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── SUB TAB: MONTHLY COMPARISON ── */}
                  {activeDashTab === "Monthly Comparison" && (
                    <>
                      <div className="row g-3 mb-4">
                        {(() => {
                          const latest = D.monthlyData[D.monthlyData.length - 1] || {};
                          const prev = D.monthlyData[D.monthlyData.length - 2] || {};
                          const createdDelta = (latest.created || 0) - (prev.created || 0);
                          const completedDelta = (latest.completed || 0) - (prev.completed || 0);
                          return [
                            { label: "Created This Month", value: latest.created || 0, sub: `${createdDelta >= 0 ? "+" : ""}${createdDelta} vs last month`, accent: "#6366f1", Icon: FaTasks },
                            { label: "Completed This Month", value: latest.completed || 0, sub: `${completedDelta >= 0 ? "+" : ""}${completedDelta} vs last month`, accent: "#10b981", Icon: FaCheckCircle },
                            { label: "Still Open", value: latest.openAtEnd || 0, sub: "Open at end of month", accent: "#ea580c", Icon: FaClock },
                            { label: "6-Month Total", value: D.monthlyData.reduce((s, m) => s + m.created, 0), sub: "Tasks created in period", accent: "#7c3aed", Icon: FaClipboardCheck },
                          ].map((c, i) => (
                            <div key={i} className="col-6 col-md-3"><StatCard {...c} /></div>
                          ));
                        })()}
                      </div>

                      {/* Row 1: Monthly Volume + Monthly by Organization */}
                      <div className="row g-3 mb-3">
                        <div className="col-12 col-xl-6">
                          <Card>
                            <CardHead label="Monthly Volume (Incoming vs Closed)"
                              right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>Last 6 months</span>}
                            />
                            <ResponsiveContainer width="100%" height={220}>
                              <BarChart data={D.monthlyData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barSize={18} barGap={4}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                <XAxis dataKey="month" tick={xTick} {...axisProps} />
                                <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                <Tooltip {...ttStyle} />
                                <Legend wrapperStyle={{ fontSize: "0.72rem", paddingTop: 10, color: T.text2 }} iconType="circle" iconSize={7} />
                                <Bar dataKey="created" name="Incoming" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="completed" name="Closed" fill="#10b981" radius={[3, 3, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Card>
                        </div>
                        <div className="col-12 col-xl-6">
                          <Card>
                            <CardHead label="Monthly by Organization"
                              right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>{D.topProjectsM.length} projects</span>}
                            />
                            <ResponsiveContainer width="100%" height={220}>
                              <BarChart data={D.monthlyByOrg} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barSize={22}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                <XAxis dataKey="month" tick={xTick} {...axisProps} />
                                <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                <Tooltip {...ttStyle} />
                                <Legend wrapperStyle={{ fontSize: "0.72rem", paddingTop: 10, color: T.text2 }} iconType="circle" iconSize={7} />
                                {D.topProjectsM.map((proj, i) => (
                                  <Bar key={proj} dataKey={proj} stackId="org"
                                    fill={["#3b82f6", "#f97316", "#10b981", "#ec4899", "#f59e0b", "#8b5cf6"][i % 6]}
                                    radius={i === D.topProjectsM.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          </Card>
                        </div>
                      </div>

                      {/* Row 2: Monthly by Category + Monthly Avg First Response */}
                      <div className="row g-3 mb-3">
                        <div className="col-12 col-xl-6">
                          <Card>
                            <CardHead label="Monthly by Category" />
                            <ResponsiveContainer width="100%" height={220}>
                              <BarChart data={D.monthlyByCategory} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barSize={22}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                <XAxis dataKey="month" tick={xTick} {...axisProps} />
                                <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                <Tooltip {...ttStyle} />
                                <Legend wrapperStyle={{ fontSize: "0.72rem", paddingTop: 10, color: T.text2 }} iconType="circle" iconSize={7} />
                                {D.allTypesM.map((tp, i) => (
                                  <Bar key={tp} dataKey={tp} stackId="cat"
                                    fill={TYPE_COLORS[i % TYPE_COLORS.length]}
                                    radius={i === D.allTypesM.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          </Card>
                        </div>
                        <div className="col-12 col-xl-6">
                          <Card>
                            <CardHead label="Monthly Avg First Response (H)"
                              right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>by task type</span>}
                            />
                            {D.timeTypesM.length === 0
                              ? <p style={{ fontSize: "0.82rem", color: T.text3 }}>No time tracking data available.</p>
                              : (
                                <ResponsiveContainer width="100%" height={220}>
                                  <LineChart data={D.monthlyAvgFirstResponse} margin={{ top: 4, right: 10, left: -18, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                    <XAxis dataKey="month" tick={xTick} {...axisProps} />
                                    <YAxis tick={yTick} {...axisProps} />
                                    <Tooltip {...ttStyle} />
                                    <Legend wrapperStyle={{ fontSize: "0.72rem", paddingTop: 10, color: T.text2 }} iconType="circle" iconSize={7} />
                                    {D.timeTypesM.map((tp, i) => (
                                      <Line key={tp} type="monotone" dataKey={tp}
                                        stroke={["#3b82f6", "#ef4444", "#f97316", "#8b5cf6", "#10b981"][i % 5]}
                                        strokeWidth={2} dot={{ r: 4, strokeWidth: 0 }}
                                        activeDot={{ r: 5, strokeWidth: 0 }}
                                        connectNulls />
                                    ))}
                                  </LineChart>
                                </ResponsiveContainer>
                              )
                            }
                          </Card>
                        </div>
                      </div>

                      {/* Row 3: Monthly Avg Time to Done (full width) */}
                      <div className="row g-3 mb-3">
                        <div className="col-12">
                          <Card>
                            <CardHead label="Monthly Avg Time to Done (H)"
                              right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>by task type · 6 months</span>}
                            />
                            {D.timeTypesM.length === 0
                              ? <p style={{ fontSize: "0.82rem", color: T.text3 }}>No time tracking data available.</p>
                              : (
                                <ResponsiveContainer width="100%" height={220}>
                                  <LineChart data={D.monthlyAvgTimeDone} margin={{ top: 4, right: 10, left: -18, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                    <XAxis dataKey="month" tick={xTick} {...axisProps} />
                                    <YAxis tick={yTick} {...axisProps} />
                                    <Tooltip {...ttStyle} />
                                    <Legend wrapperStyle={{ fontSize: "0.72rem", paddingTop: 10, color: T.text2 }} iconType="circle" iconSize={7} />
                                    {D.timeTypesM.map((tp, i) => (
                                      <Line key={tp} type="monotone" dataKey={tp}
                                        stroke={["#3b82f6", "#ef4444", "#f97316", "#8b5cf6", "#10b981"][i % 5]}
                                        strokeWidth={2} dot={{ r: 4, strokeWidth: 0 }}
                                        activeDot={{ r: 5, strokeWidth: 0 }}
                                        connectNulls />
                                    ))}
                                  </LineChart>
                                </ResponsiveContainer>
                              )
                            }
                          </Card>
                        </div>
                      </div>

                      {/* Row 4: Month-by-Month table (unchanged) */}
                      <Card>
                        <CardHead label="Month-by-Month Breakdown" />
                        <div className="table-responsive">
                          <table className="table table-borderless align-middle mb-0">
                            <thead>
                              <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                                {["Month", "Created", "Completed", "Remaining Open", "Completion Rate"].map((h, i) => (
                                  <th key={i} style={{ fontSize: "0.67rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", paddingBottom: 10, whiteSpace: "nowrap" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {[...D.monthlyData].reverse().map((m, i) => {
                                const cRate = m.created > 0 ? Math.round((m.completed / m.created) * 100) : 0;
                                return (
                                  <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                                    <td style={{ fontWeight: 600, color: T.text1, fontSize: "0.84rem" }}>{m.month}</td>
                                    <td><Chip bg="#eef2ff" color={T.primary}>{m.created}</Chip></td>
                                    <td><Chip bg="#f0fdf4" color="#166534">{m.completed}</Chip></td>
                                    <td><Chip bg={m.openAtEnd > 0 ? "#fff7ed" : T.bg} color={m.openAtEnd > 0 ? "#c2410c" : T.text3}>{m.openAtEnd}</Chip></td>
                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <div style={{ width: 60, height: 6, background: T.border, borderRadius: 3, overflow: "hidden" }}>
                                          <div style={{ height: "100%", width: `${cRate}%`, background: cRate >= 70 ? "#10b981" : cRate >= 40 ? "#f59e0b" : "#ef4444", borderRadius: 3 }} />
                                        </div>
                                        <span style={{ fontSize: "0.78rem", fontWeight: 700, color: T.text2 }}>{cRate}%</span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    </>
                  )}

                  {/* ── SUB TAB: WEEKEND ANALYSIS ── */}
                  {activeDashTab === "Weekend Analysis" && (
                    <>
                      {/* Description */}
                      <div style={{ marginBottom: 14 }}>
                        <p style={{ fontSize: "0.72rem", color: T.text3, marginBottom: 10 }}>
                          Weekend = <strong style={{ color: T.text2 }}>Sat + Sun</strong> · creation timestamps in UTC
                        </p>
                        {/* Category filter pills */}
                        <div className="d-flex flex-wrap gap-2">
                          {["All", ...D.allTypesM].map(tp => (
                            <button key={tp} onClick={() => setWeekendTypeFilter(tp)} style={{
                              padding: "4px 14px", fontSize: "0.78rem", borderRadius: 6, cursor: "pointer",
                              border: `1.5px solid ${weekendTypeFilter === tp ? T.primary : T.border}`,
                              background: weekendTypeFilter === tp ? T.primary : T.white,
                              color: weekendTypeFilter === tp ? T.white : T.text2,
                              fontWeight: weekendTypeFilter === tp ? 600 : 500,
                              transition: "all 0.15s",
                            }}>{tp}</button>
                          ))}
                        </div>
                      </div>

                      {/* Row 1: 4 volume stat cards */}
                      <div className="row g-3 mb-3">
                        {[
                          {
                            label: "Weekend Tickets", value: WD.weekendCreated,
                            sub: `${WD.total > 0 ? (WD.weekendCreated / WD.total * 100).toFixed(1) : 0}% of total`,
                            sub2: "Created Sat or Sun (UTC)", accent: "#ef4444",
                          },
                          {
                            label: "Weekday Tickets", value: WD.weekdayCreated,
                            sub: "Mon–Fri",
                            sub2: "Normal-hours volume", accent: "#6366f1",
                          },
                          {
                            label: "Weekend Close Rate", value: `${WD.weekendCloseRate}%`,
                            sub: `${WD.weekendClosed} of ${WD.weekendCreated}`,
                            sub2: "Closed proportion (weekend-created)", accent: "#f59e0b",
                          },
                          {
                            label: "Weekday Close Rate", value: `${WD.weekdayCloseRate}%`,
                            sub: `${WD.weekdayClosed} of ${WD.weekdayCreated}`,
                            sub2: "Closed proportion (weekday-created)", accent: "#10b981",
                          },
                        ].map((c, i) => (
                          <div key={i} className="col-6 col-md-3">
                            <div style={{
                              background: T.white, borderRadius: T.radius, padding: "16px 18px",
                              boxShadow: T.shadow, borderTop: `3px solid ${c.accent}`, height: "100%",
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                                <SLabel>{c.label}</SLabel>
                                <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.accent, flexShrink: 0, marginTop: 2 }} />
                              </div>
                              <div style={{ fontSize: "1.9rem", fontWeight: 800, color: T.text1, lineHeight: 1.1, marginBottom: 2 }}>{c.value}</div>
                              <div style={{ fontSize: "0.72rem", color: T.text2, marginBottom: 2 }}>{c.sub}</div>
                              <div style={{ fontSize: "0.68rem", color: T.text3 }}>{c.sub2}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Row 2: 4 compliance stat cards */}
                      <div className="row g-3 mb-3">
                        {[
                          {
                            label: "Weekend FR Compliance", value: `${WD.weekendFRRate}%`,
                            sub: `${Math.round(WD.weekendFRRate / 100 * WD.weekendFRTotal)} of ${WD.weekendFRTotal}`,
                            sub2: "First response within target", accent: "#ef4444",
                          },
                          {
                            label: "Weekday FR Compliance", value: `${WD.weekdayFRRate}%`,
                            sub: `${Math.round(WD.weekdayFRRate / 100 * WD.weekdayFRTotal)} of ${WD.weekdayFRTotal}`,
                            sub2: "First response within target", accent: "#6366f1",
                          },
                          {
                            label: "Weekend TTD Compliance", value: `${WD.weekendTTDRate}%`,
                            sub: `${Math.round(WD.weekendTTDRate / 100 * WD.weekendFRTotal)} of ${WD.weekendFRTotal}`,
                            sub2: "Resolution within target", accent: "#f59e0b",
                          },
                          {
                            label: "Weekday TTD Compliance", value: `${WD.weekdayTTDRate}%`,
                            sub: `${Math.round(WD.weekdayTTDRate / 100 * WD.weekdayFRTotal)} of ${WD.weekdayFRTotal}`,
                            sub2: "Resolution within target", accent: "#10b981",
                          },
                        ].map((c, i) => (
                          <div key={i} className="col-6 col-md-3">
                            <div style={{
                              background: T.white, borderRadius: T.radius, padding: "16px 18px",
                              boxShadow: T.shadow, borderTop: `3px solid ${c.accent}`, height: "100%",
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                                <SLabel>{c.label}</SLabel>
                                <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.accent, flexShrink: 0, marginTop: 2 }} />
                              </div>
                              <div style={{ fontSize: "1.9rem", fontWeight: 800, color: T.text1, lineHeight: 1.1, marginBottom: 2 }}>{c.value}</div>
                              <div style={{ fontSize: "0.72rem", color: T.text2, marginBottom: 2 }}>{c.sub}</div>
                              <div style={{ fontSize: "0.68rem", color: T.text3 }}>{c.sub2}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Row 3: Day of Week + Hour of Day charts */}
                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <Card>
                            <CardHead label="Tickets by Day of Week (Orange = Weekend)" />
                            <ResponsiveContainer width="100%" height={240}>
                              <BarChart data={WD.weekdayData} margin={{ top: 4, right: 8, left: -24, bottom: 0 }} barSize={32}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                <XAxis dataKey="name" tick={xTick} {...axisProps} />
                                <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                <Tooltip {...ttStyle} />
                                <Bar dataKey="tasks" name="Tickets" radius={[4, 4, 0, 0]}>
                                  {WD.weekdayData.map((entry, i) => (
                                    <Cell key={i} fill={entry.weekend ? "#f97316" : "#3b82f6"} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </Card>
                        </div>
                        <div className="col-12 col-md-6">
                          <Card>
                            <CardHead label="Tickets by Hour of Day (UTC)" />
                            <ResponsiveContainer width="100%" height={240}>
                              <BarChart data={WD.hourData} margin={{ top: 4, right: 8, left: -24, bottom: 0 }} barSize={14}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                <XAxis dataKey="hour" tick={xTick} {...axisProps} interval={2} />
                                <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                <Tooltip {...ttStyle} formatter={(v) => [v, "Tickets"]} labelFormatter={(h) => `Hour ${h}:00 UTC`} />
                                <Bar dataKey="tasks" name="Tickets" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Card>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ══════════════ TOP TAB: PROJECTS ══════════════ */}
              {activeTopTab === "Projects" && (() => {
                const totalProjects = projectStats.length;
                const avgRate = totalProjects > 0 ? Math.round(projectStats.reduce((s, p) => s + p.rate, 0) / totalProjects) : 0;
                const topProject = projectStats[0];
                return (
                  <>
                    <div className="row g-3 mb-4">
                      {[
                        { label: "Total Projects", value: totalProjects, sub: "With tasks in range", accent: T.primary, Icon: FaProjectDiagram },
                        { label: "Total Tasks", value: filteredTasks.length, sub: "Across all projects", accent: "#0284c7", Icon: FaTasks },
                        { label: "Avg Completion", value: `${avgRate}%`, sub: "Across all projects", accent: "#10b981", Icon: FaCheckCircle },
                        { label: "Most Active", value: topProject ? topProject.name.split(" ").slice(0, 2).join(" ") : "—", sub: topProject ? `${topProject.total} tasks` : "", accent: "#f59e0b", Icon: FaFire },
                      ].map((c, i) => <div key={i} className="col-6 col-md-3"><StatCard {...c} /></div>)}
                    </div>

                    <Card>
                      <CardHead label="Projects Overview" right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>{totalProjects} projects</span>} />
                      <div className="table-responsive">
                        <table className="table table-borderless align-middle mb-0">
                          <thead>
                            <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                              {["Project", "Total", "Open", "Completed", "Overdue", "Completion Rate"].map((h, i) => (
                                <th key={i} style={{ fontSize: "0.67rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", paddingBottom: 10, whiteSpace: "nowrap" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {projectStats.map((p, i) => (
                              <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                                <td style={{ fontWeight: 600, color: T.text1, fontSize: "0.84rem" }}>{p.name}</td>
                                <td><Chip bg="#eef2ff" color={T.primary}>{p.total}</Chip></td>
                                <td><Chip bg="#fff7ed" color="#c2410c">{p.open}</Chip></td>
                                <td><Chip bg="#f0fdf4" color="#166534">{p.completed}</Chip></td>
                                <td><Chip bg={p.overdue > 0 ? "#fef2f2" : T.bg} color={p.overdue > 0 ? "#dc2626" : T.text3}>{p.overdue}</Chip></td>
                                <td style={{ minWidth: 180 }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <div style={{ flex: 1, height: 7, background: T.border, borderRadius: 4, overflow: "hidden" }}>
                                      <div style={{ height: "100%", width: `${p.rate}%`, background: p.rate >= 70 ? "#10b981" : p.rate >= 40 ? "#f59e0b" : "#ef4444", borderRadius: 4, transition: "width 0.5s" }} />
                                    </div>
                                    <span style={{ fontSize: "0.78rem", fontWeight: 700, color: T.text2, minWidth: 36 }}>{p.rate}%</span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  </>
                );
              })()}

              {/* ══════════════ TOP TAB: SPRINTS ══════════════ */}
              {activeTopTab === "Sprints" && (() => {
                const activeSprints   = sprintStats.filter(s => s.status === "ACTIVE" || s.status === "IN_PROGRESS").length;
                const completedSprints = sprintStats.filter(s => s.status === "COMPLETED").length;
                const sprintBadge = s => {
                  if (s.status === "ACTIVE"    || s.status === "IN_PROGRESS") return { bg: "#f0fdf4", color: "#16a34a",  label: "Active"     };
                  if (s.status === "PLANNING"  || s.status === "PLANNED")     return { bg: "#eff6ff", color: "#2563eb",  label: "Planning"   };
                  if (s.status === "COMPLETED")                                return { bg: "#f0fdf4", color: "#475569",  label: "Completed"  };
                  if (s.status === "CANCELLED")                                return { bg: "#fef2f2", color: "#dc2626",  label: "Cancelled"  };
                  return { bg: "#f8fafc", color: T.text3, label: s.status };
                };
                return (
                  <>
                    <div className="row g-3 mb-4">
                      {[
                        { label: "Active Sprints", value: activeSprints, sub: "Currently in progress", accent: "#10b981", Icon: FaRocket },
                        { label: "Planning", value: sprintStats.filter(s => s.status === "PLANNING" || s.status === "PLANNED").length, sub: "Not yet started", accent: "#3b82f6", Icon: FaCalendarAlt },
                        { label: "Completed", value: completedSprints, sub: "Finished sprints", accent: "#475569", Icon: FaCheckCircle },
                        { label: "Total Tasks", value: filteredTasks.filter(t => t.sprintId).length, sub: "In sprints", accent: T.primary, Icon: FaTasks },
                      ].map((c, i) => <div key={i} className="col-6 col-md-3"><StatCard {...c} /></div>)}
                    </div>

                    <Card>
                      {filteredSprintStats.length === 0
                        ? <p style={{ color: T.text3, fontSize: "0.84rem", textAlign: "center", padding: "32px 0", marginBottom: 0 }}>
                            {sprints.length === 0 ? "Loading sprints…" : "No sprints match the selected filter."}
                          </p>
                        : <div className="table-responsive">
                            <table className="table table-borderless align-middle mb-0">
                              <thead>
                                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                                  {["Sprint", "Project", "Status", "Start", "End", "Progress", "Total", "Done", "Open"].map((h, i) => (
                                    <th key={i} style={{ fontSize: "0.67rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", paddingBottom: 10, whiteSpace: "nowrap" }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {filteredSprintStats.map(s => {
                                  const badge    = sprintBadge(s);
                                  const barColor = s.progress >= 80 ? "#10b981" : s.progress >= 50 ? "#f59e0b" : "#6366f1";
                                  const fmt = d => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—";
                                  return (
                                    <tr key={s.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                                      <td>
                                        <div style={{ fontWeight: 600, color: T.text1, fontSize: "0.85rem" }}>{s.name}</div>
                                        {s.goal && <div style={{ fontSize: "0.71rem", color: T.text3, marginTop: 2, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.goal}</div>}
                                      </td>
                                      <td style={{ fontSize: "0.78rem", color: T.text2, whiteSpace: "nowrap" }}>{s.projectName || "—"}</td>
                                      <td>
                                        <span style={{ background: badge.bg, color: badge.color, borderRadius: 20, padding: "2px 10px", fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap" }}>{badge.label}</span>
                                      </td>
                                      <td style={{ fontSize: "0.78rem", color: T.text2, whiteSpace: "nowrap" }}>{fmt(s.startDate)}</td>
                                      <td style={{ fontSize: "0.78rem", color: T.text2, whiteSpace: "nowrap" }}>{fmt(s.endDate)}</td>
                                      <td style={{ minWidth: 140 }}>
                                        <div className="d-flex align-items-center gap-2">
                                          <div style={{ flex: 1, height: 7, background: T.border, borderRadius: 4, overflow: "hidden" }}>
                                            <div style={{ height: "100%", width: `${s.progress}%`, background: barColor, borderRadius: 4, transition: "width 0.5s" }} />
                                          </div>
                                          <span style={{ fontSize: "0.78rem", fontWeight: 700, color: barColor, minWidth: 36 }}>{s.progress}%</span>
                                        </div>
                                      </td>
                                      <td className="text-center"><Chip bg="#eef2ff" color={T.primary}>{s.total}</Chip></td>
                                      <td className="text-center"><Chip bg="#f0fdf4" color="#166534">{s.completed}</Chip></td>
                                      <td className="text-center"><Chip bg={s.open > 0 ? "#fff7ed" : T.bg} color={s.open > 0 ? "#c2410c" : T.text3}>{s.open}</Chip></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                      }
                    </Card>
                  </>
                );
              })()}

              {/* ══════════════ TOP TAB: TIMESHEETS ══════════════ */}
              {activeTopTab === "Timesheets" && (
                <>
                  <div className="row g-3 mb-4">
                    {[
                      { label: "Total Hours Logged", value: `${timesheetStats.totalHours}h`, sub: "All time tracked", accent: T.primary, Icon: FaClock },
                      { label: "Avg per Person", value: `${timesheetStats.avgPerPerson}h`, sub: `Across ${timesheetStats.byEmployee.length} people`, accent: "#7c3aed", Icon: FaUsers },
                      { label: "This Week", value: `${timesheetStats.thisWeekH}h`, sub: "Hours logged this week", accent: "#10b981", Icon: FaCalendarAlt },
                      { label: "Top Contributor", value: timesheetStats.byEmployee[0]?.name.split(" ")[0] || "—", sub: timesheetStats.byEmployee[0] ? `${timesheetStats.byEmployee[0].total.toFixed(1)}h logged` : "", accent: "#f59e0b", Icon: FaFire },
                    ].map((c, i) => <div key={i} className="col-6 col-md-3"><StatCard {...c} /></div>)}
                  </div>

                  <div className="row g-3">
                    <div className="col-12 col-xl-7">
                      <Card>
                        <CardHead label="Hours by Person" right={<Link to="/admintimesheetgrid" style={{ fontSize: "0.72rem", color: T.primary, textDecoration: "none", fontWeight: 600 }}>View full timesheets →</Link>} />
                        {timesheetStats.byEmployee.length === 0
                          ? <p style={{ color: T.text3, fontSize: "0.84rem" }}>No worklog data available.</p>
                          : <ResponsiveContainer width="100%" height={Math.max(timesheetStats.byEmployee.slice(0,10).length * 38 + 16, 120)}>
                              <BarChart data={timesheetStats.byEmployee.slice(0, 10)} layout="vertical" margin={{ top: 0, right: 52, left: 4, bottom: 0 }} barSize={13}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={T.border} />
                                <XAxis type="number" tick={xTick} {...axisProps} />
                                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12, fill: T.text2 }} {...axisProps} />
                                <Tooltip {...ttStyle} formatter={v => [`${v}h`, "Hours"]} />
                                <Bar dataKey="total" name="Hours" fill={T.primary} radius={[0, 6, 6, 0]}
                                  label={{ position: "right", fontSize: 11, fill: T.text2, fontWeight: 700, formatter: v => `${(Math.round(v * 10) / 10)}h` }} />
                              </BarChart>
                            </ResponsiveContainer>
                        }
                      </Card>
                    </div>
                    <div className="col-12 col-xl-5">
                      <Card>
                        <CardHead label="Weekly Hours Trend" right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>last 12 weeks</span>} />
                        <ResponsiveContainer width="100%" height={240}>
                          <AreaChart data={timesheetStats.weeklyHours} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                            <defs>
                              <linearGradient id="tsGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.15} />
                                <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                            <XAxis dataKey="week" tick={xTick} {...axisProps} />
                            <YAxis tick={yTick} {...axisProps} />
                            <Tooltip {...ttStyle} formatter={v => [`${v}h`, "Hours"]} />
                            <Area type="monotone" dataKey="hours" name="Hours" stroke="#7c3aed" strokeWidth={2} fill="url(#tsGrad)" dot={{ fill: "#7c3aed", r: 3, strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 0 }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Card>
                    </div>
                  </div>
                </>
              )}

              {/* ══════════════ TOP TAB: REPORTS ══════════════ */}
              {activeTopTab === "Reports" && (
                <>
                  <div className="row g-3 mb-3">
                    {[
                      { label: "Total Tasks", value: D.total, sub: "In selected period", accent: "#475569", Icon: FaTasks },
                      { label: "Completion Rate", value: `${D.rate}%`, sub: `${D.completed} of ${D.total} done`, accent: "#10b981", Icon: FaCheckCircle },
                      { label: "Overdue", value: D.overdue, sub: "Open & past due date", accent: "#dc2626", Icon: FaExclamationTriangle },
                      { label: "Unassigned", value: D.unassigned, sub: "Need an owner", accent: "#d97706", Icon: FaUserSlash },
                      { label: "High Priority", value: D.highPri, sub: "Open high-priority", accent: "#ef4444", Icon: FaFire },
                      { label: "Critical", value: D.critical, sub: "High priority + overdue", accent: "#9f1239", Icon: FaBolt },
                      { label: "SLA Compliance", value: `${D.overallSla}%`, sub: "Tasks within SLA", accent: D.overallSla >= 80 ? "#10b981" : "#f59e0b", Icon: FaClipboardCheck },
                      { label: "Hours Logged", value: `${timesheetStats.totalHours}h`, sub: "Tracked across team", accent: "#7c3aed", Icon: FaClock },
                    ].map((c, i) => <div key={i} className="col-6 col-md-3"><StatCard {...c} /></div>)}
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-6">
                      <Card>
                        <CardHead label="Task Status Breakdown" />
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={D.statusData} dataKey="value" nameKey="name" cx="38%" innerRadius={48} outerRadius={76} paddingAngle={3}>
                              {D.statusData.map((e, i) => <Cell key={i} fill={STATUS_COLORS[e.name] || "#94a3b8"} />)}
                            </Pie>
                            <Tooltip {...ttStyle} />
                            <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "0.72rem", lineHeight: "1.9", paddingLeft: 8, color: T.text2 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Card>
                    </div>
                    <div className="col-12 col-md-6">
                      <Card>
                        <CardHead label="Priority Distribution" />
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={D.priorityData} dataKey="value" nameKey="name" cx="38%" innerRadius={48} outerRadius={76} paddingAngle={3}>
                              {D.priorityData.map((e, i) => <Cell key={i} fill={PRIORITY_COLORS[e.name] || "#94a3b8"} />)}
                            </Pie>
                            <Tooltip {...ttStyle} />
                            <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "0.72rem", lineHeight: "1.9", paddingLeft: 8, color: T.text2 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Card>
                    </div>
                  </div>

                  <Card>
                    <CardHead label="Projects Summary" right={<span style={{ fontSize: "0.72rem", color: T.text3 }}>{projectStats.length} projects</span>} />
                    <div className="table-responsive">
                      <table className="table table-borderless align-middle mb-0">
                        <thead>
                          <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                            {["Project", "Total", "Open", "Completed", "Overdue", "Completion Rate"].map((h, i) => (
                              <th key={i} style={{ fontSize: "0.67rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", paddingBottom: 10, whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {projectStats.map((p, i) => (
                            <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                              <td style={{ fontWeight: 600, color: T.text1, fontSize: "0.84rem" }}>{p.name}</td>
                              <td><Chip bg="#eef2ff" color={T.primary}>{p.total}</Chip></td>
                              <td><Chip bg="#fff7ed" color="#c2410c">{p.open}</Chip></td>
                              <td><Chip bg="#f0fdf4" color="#166534">{p.completed}</Chip></td>
                              <td><Chip bg={p.overdue > 0 ? "#fef2f2" : T.bg} color={p.overdue > 0 ? "#dc2626" : T.text3}>{p.overdue}</Chip></td>
                              <td style={{ minWidth: 180 }}>
                                <div className="d-flex align-items-center gap-2">
                                  <div style={{ flex: 1, height: 6, background: T.border, borderRadius: 3, overflow: "hidden" }}>
                                    <div style={{ height: "100%", width: `${p.rate}%`, background: p.rate >= 70 ? "#10b981" : p.rate >= 40 ? "#f59e0b" : "#ef4444", borderRadius: 3, transition: "width 0.5s" }} />
                                  </div>
                                  <span style={{ fontSize: "0.78rem", fontWeight: 700, color: p.rate >= 70 ? "#10b981" : p.rate >= 40 ? "#f59e0b" : "#ef4444", minWidth: 36 }}>{p.rate}%</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </>
              )}

              {/* ══════════════ TOP TAB: TEAM ══════════════ */}
              {activeTopTab === "Team" && (() => {
                const activeAssignees = filteredTeamData.filter(m => m.open > 0).length;
                const teamTotalOpen = filteredTeamData.reduce((s, m) => s + m.open, 0);
                const teamOverdue = filteredTeamData.reduce((s, m) => s + m.overdue, 0);
                const teamCritical = filteredTeamData.reduce((s, m) => s + m.critical, 0);
                const teamCompleted = filteredTeamData.reduce((s, m) => s + m.completed, 0);
                const avgLoad = activeAssignees > 0 ? (teamTotalOpen / activeAssignees).toFixed(1) : "0";

                const teamStatCards = [
                  { label: "Active Assignees", value: activeAssignees, sub: "Carrying work", sub2: `${activeAssignees} people have open tasks`, dotColor: T.text3, valColor: T.text1 },
                  { label: "Total Open", value: teamTotalOpen, sub: "Across team", sub2: `${teamTotalOpen} open tasks in flight`, dotColor: "#ea580c", valColor: "#ea580c" },
                  { label: "Overdue", value: teamOverdue, sub: "Past due", sub2: `${teamOverdue} tasks past due date`, dotColor: "#dc2626", valColor: "#dc2626" },
                  { label: "Critical Open", value: teamCritical, sub: "Need attention", sub2: `${teamCritical} high-urgency tasks open`, dotColor: "#dc2626", valColor: "#dc2626" },
                  { label: "Completed", value: teamCompleted, sub: `Last ${timeRange === "All" ? "all time" : timeRange}`, sub2: `${teamCompleted} tasks closed`, dotColor: "#16a34a", valColor: "#16a34a" },
                  { label: "Avg Load", value: avgLoad, sub: "Per active person", sub2: `${avgLoad} open tasks per person on average`, dotColor: T.text1, valColor: T.text1 },
                ];

                const togglePriChip = (p) => setPriChips(prev =>
                  prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
                );

                return (
                  <>
                    {/* Summary stat cards */}
                    <div className="row g-3 mb-4">
                      {teamStatCards.map((c, i) => (
                        <div key={i} className="col-6 col-md-2">
                          <div style={{
                            background: T.white, borderRadius: T.radius, padding: "16px 18px",
                            boxShadow: T.shadow, position: "relative", height: "100%",
                            borderTop: `3px solid ${c.dotColor}`,
                          }}>
                            <div style={{ position: "absolute", top: 13, right: 13, width: 8, height: 8, borderRadius: "50%", background: c.dotColor }} />
                            <p style={{ fontSize: "0.65rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>{c.label}</p>
                            <p style={{ fontSize: "1.9rem", fontWeight: 800, color: c.valColor, lineHeight: 1.05, marginBottom: 2 }}>{c.value}</p>
                            <p style={{ fontSize: "0.72rem", color: T.text2, marginBottom: 2 }}>{c.sub}</p>
                            <p style={{ fontSize: "0.68rem", color: T.text3, marginBottom: 0 }}>{c.sub2}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Team filter bar */}
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-3 px-4 py-3"
                      style={{ background: T.white, borderRadius: T.radius, boxShadow: T.shadow, border: `1px solid ${T.border}` }}>

                      <span style={{ fontSize: "0.78rem", color: T.text2, whiteSpace: "nowrap" }}>
                        Showing <strong style={{ color: T.primary }}>{filteredTeamData.length}</strong> assignees
                      </span>

                      <div style={{ width: 1, height: 20, background: T.border }} />

                      {/* Load filter */}
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: "0.72rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.06em" }}>Load:</span>
                        <div style={{ display: "inline-flex", border: `1.5px solid ${T.border}`, borderRadius: 6, overflow: "hidden" }}>
                          {["All", "Heavy", "Medium", "Normal"].map((opt, i) => (
                            <button key={opt} onClick={() => setLoadFilter(opt)} style={{
                              padding: "4px 12px", fontSize: "0.75rem", border: "none",
                              borderLeft: i > 0 ? `1px solid ${T.border}` : "none",
                              background: loadFilter === opt ? T.primary : "transparent",
                              color: loadFilter === opt ? T.white : T.text2,
                              cursor: "pointer", fontWeight: loadFilter === opt ? 700 : 500,
                              transition: "all 0.12s",
                            }}>{opt}</button>
                          ))}
                        </div>
                      </div>

                      <div style={{ width: 1, height: 20, background: T.border }} />

                      {/* Priority chips */}
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: "0.72rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.06em" }}>Priority:</span>
                        <div className="d-flex gap-1">
                          {[
                            { key: "High", label: "H", bg: "#fef2f2", activeBg: "#dc2626", color: "#dc2626", activeColor: "#fff" },
                            { key: "Medium", label: "M", bg: "#fffbeb", activeBg: "#b45309", color: "#b45309", activeColor: "#fff" },
                            { key: "Low", label: "L", bg: "#f0fdf4", activeBg: "#166534", color: "#166534", activeColor: "#fff" },
                          ].map(p => {
                            const isActive = priChips.includes(p.key);
                            return (
                              <button key={p.key} onClick={() => togglePriChip(p.key)} title={p.key} style={{
                                width: 28, height: 28, borderRadius: "50%", border: "none",
                                background: isActive ? p.activeBg : p.bg,
                                color: isActive ? p.activeColor : p.color,
                                fontSize: "0.72rem", fontWeight: 800, cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                transition: "all 0.15s",
                              }}>{p.label}</button>
                            );
                          })}
                        </div>
                      </div>

                      {anyTeamFilter && (
                        <button onClick={() => { setFilterAssignee(""); setTeamStatusFilter("Active"); setLoadFilter("All"); setPriChips([]); setTeamFilterProject(""); setTeamFilterType(""); setDaysActiveFilter("All"); }}
                          style={{ fontSize: "0.72rem", color: T.text3, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0, marginLeft: "auto" }}>
                          Reset all
                        </button>
                      )}
                    </div>

                    {/* Team table */}
                    <Card>
                      {filteredTeamData.length === 0
                        ? (
                          <div className="text-center py-4">
                            <p style={{ fontSize: "0.9rem", color: T.text3, marginBottom: 0 }}>No assignees match the current filters.</p>
                          </div>
                        )
                        : (
                          <div className="table-responsive">
                            <table className="table table-borderless align-middle mb-0">
                              <thead>
                                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                                  {[
                                    { h: "Assignee", c: "", w: undefined },
                                    { h: "Open ↓", c: "text-center", w: undefined },
                                    { h: "Priority Mix", c: "", w: undefined },
                                    { h: "Load Score", c: "", w: 140 },
                                    { h: "Overdue", c: "text-center", w: undefined },
                                    { h: "Critical", c: "text-center", w: undefined },
                                    { h: "Days Active", c: "text-center", w: undefined },
                                    { h: "Aging", c: "text-center", w: undefined },
                                    { h: "Completed", c: "text-center", w: undefined },
                                    { h: "Logged Time", c: "text-center", w: undefined },
                                  ].map(({ h, c, w }, i) => (
                                    <th key={i} className={c} style={{
                                      fontSize: "0.67rem", fontWeight: 700, color: T.text3,
                                      textTransform: "uppercase", letterSpacing: "0.07em",
                                      paddingTop: 8, paddingBottom: 12, whiteSpace: "nowrap", minWidth: w,
                                    }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {filteredTeamData.map((m) => {
                                  const pct = maxLoadScore > 0 ? Math.round((m.loadScore / maxLoadScore) * 100) : 0;
                                  const bCol = pct > 65 ? "#ef4444" : pct > 35 ? "#f59e0b" : "#10b981";
                                  const isExp = expandedId === m.id;
                                  const aTasks = isExp ? getAssigneeTasks(m.id) : [];
                                  return (
                                    <React.Fragment key={m.id}>
                                      <tr
                                        onClick={() => setExpandedId(p => p === m.id ? null : m.id)}
                                        style={{
                                          borderBottom: `1px solid ${T.border}`,
                                          cursor: "pointer",
                                          background: isExp ? "#fafbff" : "transparent",
                                          transition: "background 0.12s",
                                        }}
                                        onMouseEnter={e => { if (!isExp) e.currentTarget.style.background = T.bg; }}
                                        onMouseLeave={e => { if (!isExp) e.currentTarget.style.background = "transparent"; }}
                                      >
                                        <td style={{ paddingTop: 12, paddingBottom: 12 }}>
                                          <div className="d-flex align-items-center gap-2">
                                            {isExp
                                              ? <FaChevronDown size={10} color={T.primary} />
                                              : <FaChevronRight size={10} color={T.text3} />}
                                            <div style={{
                                              width: 30, height: 30, borderRadius: "50%",
                                              background: `${T.primary}20`, color: T.primary,
                                              display: "flex", alignItems: "center", justifyContent: "center",
                                              fontSize: "0.73rem", fontWeight: 800, flexShrink: 0,
                                            }}>
                                              {m.name.charAt(0).toUpperCase()}
                                            </div>
                                            <span style={{ fontWeight: 600, color: T.text1, fontSize: "0.85rem" }}>{m.name}</span>
                                          </div>
                                        </td>
                                        <td className="text-center"><Chip bg="#eff6ff" color="#2563eb">{m.open}</Chip></td>
                                        <td>
                                          <div className="d-flex gap-1 flex-wrap">
                                            {m.high > 0 && <Chip bg="#fef2f2" color="#dc2626">{m.high}</Chip>}
                                            {m.medium > 0 && <Chip bg="#fffbeb" color="#b45309">{m.medium}</Chip>}
                                            {m.low > 0 && <Chip bg="#f0fdf4" color="#166534">{m.low}</Chip>}
                                            {m.open === 0 && <span style={{ color: T.text3, fontSize: "0.75rem" }}>—</span>}
                                          </div>
                                        </td>
                                        <td>
                                          <div className="d-flex align-items-center gap-2">
                                            <div style={{ flex: 1, height: 7, background: T.border, borderRadius: 4, overflow: "hidden", minWidth: 70 }}>
                                              <div style={{ height: "100%", width: `${pct}%`, background: bCol, borderRadius: 4, transition: "width 0.5s ease" }} />
                                            </div>
                                            <span style={{ fontSize: "0.76rem", fontWeight: 700, color: bCol, minWidth: 34 }}>{m.loadScore}</span>
                                          </div>
                                        </td>
                                        <td className="text-center"><Chip bg={m.overdue > 0 ? "#fef2f2" : T.bg} color={m.overdue > 0 ? "#dc2626" : T.text3}>{m.overdue}</Chip></td>
                                        <td className="text-center"><Chip bg={m.critical > 0 ? "#fef2f2" : T.bg} color={m.critical > 0 ? "#dc2626" : T.text3}>{m.critical}</Chip></td>
                                        <td className="text-center">
                                          <span style={{
                                            fontSize: "0.78rem", fontWeight: 600,
                                            color: m.daysActive > 90 ? "#7c3aed" : m.daysActive >= 30 ? "#0284c7" : T.text2,
                                          }}>
                                            {m.daysActive > 0 ? `${m.daysActive}d` : "—"}
                                          </span>
                                        </td>
                                        <td className="text-center">
                                          <span style={{ fontSize: "0.8rem", fontWeight: 600, color: m.maxAge > 30 ? "#dc2626" : T.text2 }}>
                                            {m.maxAge > 0 ? `${m.maxAge}d` : "—"}
                                          </span>
                                        </td>
                                        <td className="text-center"><Chip bg="#f0fdf4" color="#166534">{m.completed}</Chip></td>
                                        <td className="text-center">
                                          {(() => {
                                            const h = worklogByAssignee[m.id];
                                            return h > 0
                                              ? <span style={{ fontSize: "0.8rem", fontWeight: 600, color: T.text2 }}>{h % 1 === 0 ? h : h.toFixed(1)}h</span>
                                              : <span style={{ fontSize: "0.8rem", color: T.text3 }}>—</span>;
                                          })()}
                                        </td>
                                      </tr>

                                      {/* Expanded task rows */}
                                      {isExp && (
                                        <tr style={{ background: "#f8faff" }}>
                                          <td colSpan={10} style={{ padding: "8px 16px 20px 56px", borderBottom: `2px solid ${T.primary}20` }}>
                                            <p style={{ fontSize: "0.76rem", color: T.text2, marginBottom: 10, fontWeight: 500 }}>
                                              <strong style={{ color: T.primary }}>{aTasks.length}</strong> open {aTasks.length === 1 ? "task" : "tasks"} — sorted by priority then due date
                                            </p>
                                            {aTasks.length === 0
                                              ? <p style={{ fontSize: "0.82rem", color: T.text3 }}>No open tasks.</p>
                                              : (
                                                <div className="table-responsive">
                                                  <table className="table table-borderless mb-0" style={{ fontSize: "0.8rem" }}>
                                                    <thead>
                                                      <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                                                        {["Task Code", "Title", "Project", "Type", "Priority", "Status", "Due Date", "Logged"].map((h, i) => (
                                                          <th key={i} style={{ fontSize: "0.65rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.05em", paddingBottom: 8, whiteSpace: "nowrap" }}>{h}</th>
                                                        ))}
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {aTasks.map((t) => {
                                                        const sb = STATUS_BADGE[t.status] || { bg: T.bg, color: T.text2 };
                                                        const pb = PRIORITY_BADGE[t.priority] || { bg: T.bg, color: T.text2 };
                                                        const due = t.dueDate ? new Date(t.dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—";
                                                        const isOvr = t.dueDate && new Date(t.dueDate) < today;
                                                        return (
                                                          <tr key={t.id} style={{ borderBottom: `1px solid ${T.bg}` }}>
                                                            <td>
                                                              <a
                                                                href={`${window.location.origin}/tasks?taskId=${t.id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: T.primary, fontWeight: 700, textDecoration: "none", fontSize: "0.76rem" }}
                                                              >
                                                                {t.taskCode || `#${t.id}`}
                                                              </a>
                                                            </td>
                                                            <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: T.text1, fontWeight: 500 }}>{t.title}</td>
                                                            <td style={{ color: T.text2, whiteSpace: "nowrap" }}>{t.projectName || "—"}</td>
                                                            <td style={{ color: T.text3, whiteSpace: "nowrap", fontSize: "0.72rem" }}>{t.taskType || "—"}</td>
                                                            <td><span style={{ ...pb, borderRadius: 20, padding: "2px 9px", fontSize: "0.71rem", fontWeight: 700 }}>{t.priority || "—"}</span></td>
                                                            <td><span style={{ ...sb, borderRadius: 20, padding: "2px 9px", fontSize: "0.71rem", fontWeight: 600, whiteSpace: "nowrap" }}>{(t.status || "").replace(/_/g, " ")}</span></td>
                                                            <td><span style={{ fontSize: "0.78rem", fontWeight: 600, color: isOvr ? "#dc2626" : T.text2, whiteSpace: "nowrap" }}>{due}</span></td>
                                                            <td>{(() => {
                                                              const taskHours = worklogs
                                                                .filter(w => Number(w.taskId) === Number(t.id) && w.hours > 0)
                                                                .reduce((s, w) => s + w.hours, 0);
                                                              return taskHours > 0
                                                                ? <span style={{ fontSize: "0.76rem", fontWeight: 600, color: "#7c3aed" }}>{taskHours % 1 === 0 ? taskHours : taskHours.toFixed(1)}h</span>
                                                                : <span style={{ fontSize: "0.76rem", color: T.text3 }}>—</span>;
                                                            })()}</td>
                                                          </tr>
                                                        );
                                                      })}
                                                    </tbody>
                                                  </table>
                                                </div>
                                              )}
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                    </Card>
                  </>
                );
              })()}
        </div>
        )}
      </div>
      </div>{/* end flex wrapper */}

      <style>{`
        .cb-scroll::-webkit-scrollbar { width: 6px; }
        .cb-scroll::-webkit-scrollbar-track { background: transparent; }
        .cb-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .cb-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .cb-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
      `}</style>
    </>
  );
};

export default TasksLandingPage;